import React, {
  useEffect,
  useMemo,
  useCallback,
  useState,
  useContext,
} from "react";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Container from "react-bootstrap/Container";
import ListGroup from "react-bootstrap/ListGroup";
import { FaSearch, FaCheck } from "react-icons/fa";
import "./CodeSearch.css";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import { useSelector, useDispatch } from "react-redux";
import { useDebounce } from "use-debounce";
import { useLazyGetData2Query } from "../../../store/apiSlice";
import {
  clusterIdGenerator,
  prioritizeParts,
  smartSplitWithBracket,
} from "./utils";
import {
  updateCodePathHistory,
  clearCodePathHistory,
  handleSelectedCodeDetails,
  handleStoreParentAndChilds,
  setChildFilterTerm,
  updateFinalScreenState,
  removeSecondLastFromHistory,
  handleResetSelectedDetails,
  updateLocalState,
  resetLocalState,
  setSelectedCategory,
  setSelectedIndex,
  setFilteredCodes,
  clearSelectedCodeDetails,
  resetInstructions,
  clearPendingInstructions,
  setPendingInstructions,
  setCurrentWarningMessage,
  clearWarningMessage,
  setArrivedViaBackToFinal,
  setRangeChildren,
  setInitialInjuryState,
  setIsInjuryWithoutActivityFlow,
  setNextRangeToProcess,
  setInjuryFlowTracker,
  setInjuryCodeFlowTracker,
  setFinalRangeExecuted,
  setCurrentClusterId,
  addToClusterHistory,
} from "./redux/codeSearchSlice";
import {
  setRawSearchQuery,
  setSearchByCodeId,
  updateAcsCode,
} from "../../CodeBook/redux/CodeBookSlicer";
import {
  handleResetSearch,
  setActiveTab,
  setActiveIcon,
} from "../../CodeBook/redux/CodeBookSlicer";
import CodeBookUsa from "../../CodeBook/CodeBookUsa";
import { CodeContext } from "../../pages/Coder/Coder";

const normalizeCodeObject = (code) => {
  if (!code) return null;
  return {
    ...code,
    rawName: code.rawName || code.name || "",
    rawDescription: code.rawDescription || code.description || "",
  };
};

const CodeSearchUSA = React.memo(
  ({
    setShowCodeSearch,
    onFinalizeCode,
    codingscreendata,
    setfilecode,
    setIsFetchingData1,
    isFetchingData1,
    enableFinalize,
    handleCodeAssistAdding,
    diagnosisStates,
  }) => {
    // Replace useReducer with Redux selectors
    const localState = useSelector((state) => state.codeSearch);
    const {
      icd10amTabularData,
      icd10amIndexData,
      achiIndexData13,
      achiTabularData13,
      codePathHistory,
      pcsIndexData,
      achiTabularData,
      pcsTabularData,
    } = useSelector((state) => ({
      ...state.codeBook,
      codePathHistory: state.codeSearch?.codePathHistory || [],
    }));
    // console.log(pcsIndexData, "");

    const {
      slectedParntesandChilds,
      childFilterTerm,
      arrivedViaBackToFinal,
      injuryWithActivity,
      externalCauseCodes,
      placeOfOccurrence,
      activityCodes,
      injuryWithoutActivity,
      initialInjuryState,
      isInjuryWithoutActivityFlow,
      nextRangeToProcess,
      injuries,
      burns,
      frostbite,
      poisoning,
      effectsOfExternalCauses,
      earlyComplicationsOfTrauma,
      complicationOfSurgicalAndMedicalCare,
      otherComplicationOfTrauma,
      sequelaeOfInjuries,
      injuryFlowTracker,
      injuryCodeFlowTracker,
      finalRangeExecuted,
      currentClusterId,
    } = useSelector((state) => state.codeSearch);
    const rangeChildren = useMemo(
      () => ({
        injuryWithActivity,
        externalCauseCodes,
        placeOfOccurrence,
        activityCodes,
        injuryWithoutActivity,
        injuries,
        burns,
        frostbite,
        poisoning,
        effectsOfExternalCauses,
        earlyComplicationsOfTrauma,
        complicationOfSurgicalAndMedicalCare,
        otherComplicationOfTrauma,
        sequelaeOfInjuries,
      }),
      [
        injuryWithActivity,
        externalCauseCodes,
        placeOfOccurrence,
        activityCodes,
        injuryWithoutActivity,
        injuries,
        burns,
        frostbite,
        poisoning,
        effectsOfExternalCauses,
        earlyComplicationsOfTrauma,
        complicationOfSurgicalAndMedicalCare,
        otherComplicationOfTrauma,
        sequelaeOfInjuries,
      ]
    );
    const reduxDispatch = useDispatch();
    const [isBackProcessing, setIsBackProcessing] = useState(false);
    const [fetchData1] = useLazyGetData2Query();
    const [startNewCycle, setStartNewCycle] = useState(false);
    const [noInjuryFlowState, setNoInjuryFlowState] = useState(false);
    const restartInjuryCycle = sessionStorage.getItem("newcycle") === "true";
    const lastCodedEpisode = useSelector((state) => state.episodeList);
    const typeOfPatient = lastCodedEpisode?.selectedEpisode?.type_patient;
    const {
      searchTerm,
      selectedIndex,
      filteredCodes,
      selectedCategory,
      currentCodeSearchData,
      showFinalScreen,
      selectedFinalCode,
      initialSearchDone,
      selectedCodes: localSelectedCodes,
      showError,
      selectedParent,
      topLevelParent,
      lastAddedCode,
      originalCode,
      currentPath,
      // nextRangeToProcess
    } = localState;

    const dataSource = useMemo(() => {
      const isOutPatient = typeOfPatient === "outPatient";

      // -------------------------------
      // OutPatient Logic
      // -------------------------------
      if (isOutPatient) {
        if (selectedCategory === "CPT Tabular") {
          return currentCodeSearchData || achiTabularData || [];
        }

        if (selectedCategory === "PCS Codes") {
          return currentCodeSearchData || pcsIndexData || [];
        }
      }

      // -------------------------------
      // InPatient Logic (Default)
      // -------------------------------
      if (
        selectedCategory === "Principal Diagnosis" ||
        selectedCategory === "Additional Diagnoses"
      ) {
        return currentCodeSearchData || icd10amIndexData || [];
      }

      if (selectedCategory === "Procedure Codes") {
        return currentCodeSearchData || pcsIndexData || [];
      }

      return [];
    }, [
      typeOfPatient,
      selectedCategory,
      currentCodeSearchData,
      icd10amIndexData,
      pcsIndexData,
      achiTabularData,
    ]);

    // console.log(selectedCategory, "allstates", achiTabularData, localState);

    const filterCodes = useMemo(
      () => (term, data, isInitialSearchDone) => {
        if (!(term.length >= 3 && !isInitialSearchDone))
          return isInitialSearchDone ? data : [];

        const normalizedTerm = term.toLowerCase().replace(/\s+/g, " ").trim();
        const normalizeText = (text) =>
          (text || "").toLowerCase().replace(/\s+/g, " ").trim();

        //decide which field to search
        const getParentSearchField = (item) =>
          selectedCategory === "CPT Tabular" ? item.description : item.name;

        const getChildSearchField = (child) =>
          selectedCategory === "CPT Tabular" ? child.description : child.name;

        const results = data.flatMap((item) => {
          // PARENT MATCH
          const parentMatches = normalizeText(
            getParentSearchField(item)
          ).startsWith(normalizedTerm)
            ? [
                {
                  _type: "parent",
                  id: item.id,
                  name: item.name,
                  description: item.description,
                  rawName: item.name, // ✅ always name
                  rawDescription: item.description, // ✅ always description
                  children: item.subcodes || item.children || [],
                  Next_parent_code: item.Next_parent_code,
                  Warning_message: item.Warning_message,
                  see: item.see,
                  seealso: item.seealso,
                  acs: item.acs,
                  code_first_instruction: item.code_first_instruction,
                  use_additional_instruction: item.use_additional_instruction,
                  codealsowhenperf_instruction:
                  item.codealsowhenperf_instruction,
                  index_to_tabular_instruction:item.index_to_tabular_instruction,
                  isFromTabular: selectedCategory === "CPT Tabular",
                },
              ]
            : [];

          // CHILD MATCHES
          const childMatches = (item.subcodes || item.children || [])
            .filter((child) =>
              normalizeText(getChildSearchField(child)).startsWith(
                normalizedTerm
              )
            )
            .map((child) => ({
              _type: "child",
              id: child.id,
              name: child.name,
              description: child.description,
              rawName: child.name, // ✅ always name
              rawDescription: child.description, // ✅ always description
              children: item.subcodes || item.children || [],
              Next_parent_code: child.Next_parent_code,
              Warning_message: child.Warning_message,
              see: child.see,
              seealso: child.seealso,
              parentName: item.name,
              parentDescription: item.description,
              acs: child.acs,
              parentId: item.id,
              parentAcs: item.acs,
              isFromTabular: selectedCategory === "CPT Tabular",
            }));

          return [...parentMatches, ...childMatches];
        });

        return results.sort((a, b) => {
          const order = (item) => {
            if (item._type === "parent" && !item.see) return 1;
            if (item._type === "parent" && item.see) return 2;
            if (item._type === "child" && item.see) return 3;
            if (item._type === "child" && !item.see) return 4;
            return 5;
          };
          const diff = order(a) - order(b);
          if (diff !== 0) return diff;

          return (a.rawName || "").localeCompare(b.rawName || "");
        });
      },
      [selectedCategory] // 🔥 this makes search update when category changes
    );

    const findTabularChildrenWithHyphen = useCallback(
      (items, normalizedCode, level = 0, results = []) => {
        items.forEach((item) => {
          if (item.chapter && item.codes) {
            findTabularChildrenWithHyphen(
              item.codes,
              normalizedCode,
              level + 1,
              results
            );
            return;
          }

          const itemCodeNormalized = (item.name || "")
            .replace(/\./g, "")
            .toLowerCase();
          const matchesCode =
            itemCodeNormalized === normalizedCode ||
            itemCodeNormalized.startsWith(normalizedCode);

          if (matchesCode) {
            const children = item.subcodes || item.children || [];
            (children.length > 0 ? children : [item]).forEach((target) =>
              results.push({
                rawName: target.name || item.name,
                rawDescription: target.description || item.description,
                name: target.name || item.name,
                description: target.description || item.description,
                children: target.subcodes || target.children || [],
                isFromTabular: true,
                id: target.id || item.id,
                acs: target.acs || item.acs,
                ...(target === item
                  ? {}
                  : {
                      code_first_instruction: target.code_first_instruction,
                      use_additional_instruction:
                        target.use_additional_instruction,
                      codealsowhenperf_instruction:
                        target.codealsowhenperf_instruction,
                    }),
              })
            );
          }
          // Search all possible nested structures
          [item.codes, item.subcodes, item.children]
            .filter(Boolean)
            .forEach((nested) =>
              findTabularChildrenWithHyphen(
                nested,
                normalizedCode,
                level + 1,
                results
              )
            );
        });

        return results;
      },
      []
    );

    const compareCodes = (code, start, end) => {
      // Convert to comparable string: remove dots and uppercase
      const c = code.replace(/\./g, "").toUpperCase();
      const s = start.replace(/\./g, "").toUpperCase();
      const e = end.replace(/\./g, "").toUpperCase();
      return c >= s && c <= e;
    };
    const findTabularChildren = useCallback(
      (
        items,
        normalizedCode,
        results = [],
        found = { value: false },
        extraOptionToIdentify = null
      ) => {
        const isRange = normalizedCode?.includes("-");
        let rangeStart, rangeEnd;

        if (isRange) {
          const parts = normalizedCode
            .split("-")
            .map((c) => c.trim()?.toUpperCase());
          [rangeStart, rangeEnd] = [parts[0], parts[1]];
        } else {
          normalizedCode = normalizedCode?.toUpperCase();
        }

        const createResultItem = (item, isChild = false) => ({
          rawName: item.name,
          rawDescription: item.description,
          name: item.name,
          description: item.description,
          children: item.subcodes || item.children || [],
          isFromTabular: true,
          id: item.id,
          acs: item.acs,
          ...(extraOptionToIdentify &&
            !isChild && {
              code_first_instruction: item.code_first_instruction,
              use_additional_instruction: item.use_additional_instruction,
            }),
          codealsowhenperf_instruction: item.codealsowhenperf_instruction,
        });

        const isInRange = (code) => {
          if (!isRange) return false;
          return rangeEnd
            ? compareCodes(code, rangeStart, rangeEnd)
            : code.startsWith(rangeStart);
        };

        for (const item of items) {
          if (found.value) break;

          // Handle chapters
          if (item.chapter && item.codes) {
            const chapterCode = item.chapter?.replace(/\./g, "")?.toUpperCase();
            if (!isRange && chapterCode === normalizedCode) {
              findTabularChildren(
                item.codes,
                normalizedCode,
                results,
                found,
                extraOptionToIdentify
              );
              found.value = true;
              return results;
            }
            continue;
          }

          const itemCode = item.name;
          if (!itemCode) continue;

          const itemCodeNormalized = itemCode.replace(/\./g, "").toUpperCase();
          const matches =
            itemCodeNormalized === normalizedCode ||
            isInRange(itemCodeNormalized);

          if (matches) {
            const itemChildren = item.subcodes || item.children || [];
            const targets = itemChildren.length > 0 ? itemChildren : [item];

            targets.forEach((target) =>
              results.push(createResultItem(target, itemChildren.length > 0))
            );
            found.value = true;
          }

          // Recurse deeper if not found
          if (!found.value) {
            [item.codes, item.subcodes, item.children]
              .filter(Boolean)
              .forEach((nested) =>
                findTabularChildren(
                  nested,
                  normalizedCode,
                  results,
                  found,
                  extraOptionToIdentify
                )
              );
          }
        }

        return results;
      },
      []
    );

    // Recursive function to get ALL children from a code (including nested children)
    const getAllNestedChildren = useCallback((code) => {
      if (!code) return [];

      let allChildren = [];

      // Add the current code itself if it's a leaf node (no children)
      const hasChildren =
        (code.children && code.children.length > 0) ||
        (code.subcodes && code.subcodes.length > 0);

      if (!hasChildren) {
        allChildren.push(code);
      }

      // Recursively get children from subcodes
      if (code.subcodes && code.subcodes.length > 0) {
        code.subcodes.forEach((subcode) => {
          allChildren = [...allChildren, ...getAllNestedChildren(subcode)];
        });
      }

      // Recursively get children from children array
      if (code.children && code.children.length > 0) {
        code.children.forEach((child) => {
          allChildren = [...allChildren, ...getAllNestedChildren(child)];
        });
      }

      return allChildren;
    }, []);
    const filterTabularCodes = useCallback(
      (baseCode, category) => {
        // if (category === "Procedure Codes") return [];
        const normalizedCode = baseCode.replace(/\./g, "").toLowerCase();
        const tabularData =
          category === "Procedure Codes" || category === "PCS Codes" ? pcsTabularData : icd10amTabularData;
        return findTabularChildren(tabularData || [], normalizedCode);
      },
      [icd10amTabularData,pcsTabularData, findTabularChildren]
    );

    // Recursive function to get ALL codes with complete hierarchy
    const getAllCodesWithHierarchy = useCallback((code, depth = 0) => {
      if (!code) return [];

      let allCodes = [];

      // Add the current code itself
      allCodes.push({
        ...code,
        _depth: depth,
        _isRangeCode: true,
        _isParentNode:
          (code.children && code.children.length > 0) ||
          (code.subcodes && code.subcodes.length > 0),
      });

      // Recursively process subcodes (this is where intermediate parents like S00.0 are)
      if (code.subcodes && code.subcodes.length > 0) {
        code.subcodes.forEach((subcode) => {
          const subcodeResults = getAllCodesWithHierarchy(subcode, depth + 1);
          allCodes = [...allCodes, ...subcodeResults];
        });
      }

      // Recursively process children
      if (code.children && code.children.length > 0) {
        code.children.forEach((child) => {
          const childResults = getAllCodesWithHierarchy(child, depth + 1);
          allCodes = [...allCodes, ...childResults];
        });
      }

      return allCodes;
    }, []);

    // Enhanced function to get ALL codes from a range with complete hierarchy
    const getAllCodesFromRange = useCallback(
      (rangeCode, category) => {
        // First, get the main range children
        const rangeChildren = filterTabularCodes(rangeCode, category);

        if (!rangeChildren || rangeChildren.length === 0) {
          return [];
        }

        let allCodes = [];

        // For each child in the range, get complete hierarchy
        rangeChildren.forEach((child) => {
          const hierarchyCodes = getAllCodesWithHierarchy(child);
          allCodes = [...allCodes, ...hierarchyCodes];
        });

        // Remove duplicates based on ID while preserving hierarchy
        const uniqueCodes = allCodes.filter(
          (code, index, self) =>
            index === self.findIndex((c) => c.id === code.id)
        );

        return uniqueCodes;
      },
      [filterTabularCodes, getAllCodesWithHierarchy]
    );

    // Fetch range data on component mount
    // Fetch range data on component mount - enhanced to include parents
    const fetchAndStoreRangeChildren = useCallback(
      (selectedCategory = "Principal Diagnosis") => {
        const ranges = [
          {
            code: "S00-S09,S10-S29,S20-S29,S30-S39,S40-S49,S50-S59,S60-S69,S70-S79,S80-S89,S90-S99,T00-T07,T08-T14,T15-T19,T20-T31,T33-T35,T36-T50,T51-T65",
            name: "Injury Codes with Activity",
            stateKey: "injuryWithActivity",
          },
          {
            code: "V00-X59,X60-X84,X85-Y09,Y10-Y34,Y35-Y36,Y37-Y37,Y40-Y84,Y85-Y89,Y90,Y91,Y95,Y96,Y97,Y98",
            name: "External Cause Codes",
            stateKey: "externalCauseCodes",
          },
          {
            code: "Y92.0,Y92.1,Y92.2,Y92.3,Y92.4,Y92.5,Y92.6,Y92.7,Y92.8",
            name: "Place of Occurrence",
            stateKey: "placeOfOccurrence",
          },
          {
            code: "U50-U72,U73.0,U73.1,U73.2,U73.81,U73.8, U73.9",
            name: "Activity Codes",
            stateKey: "activityCodes",
          },
          {
            code: "T66-T78,T79-T79,T80-T88,T89-T89,T90-T98",
            name: "Injury Codes without Activity",
            stateKey: "injuryWithoutActivity",
          },
          {
            code: "S00-S09,S10-S29,S20-S29,S30-S39,S40-S49,S50-S59,S60-S69,S70-S79,S80-S89,S90-S99,T00-T07,T08-T14",
            name: "injuries",
            stateKey: "injuries",
          },
          {
            code: "T20-T31",
            name: "burns",
            stateKey: "burns",
          },
          {
            code: "T33-T35",
            name: "frostbite",
            stateKey: "frostbite",
          },
          {
            code: "T33-T35",
            name: "poisoning",
            stateKey: "poisoning",
          },
          {
            code: "T66-T78",
            name: "effectsOfExternalCauses",
            stateKey: "effectsOfExternalCauses",
          },
          {
            code: "T79-T79",
            name: "earlyComplicationsOfTrauma",
            stateKey: "earlyComplicationsOfTrauma",
          },
          {
            code: "T80-T88",
            name: "complicationOfSurgicalAndMedicalCare",
            stateKey: "complicationOfSurgicalAndMedicalCare",
          },
          {
            code: "T89-T89",
            name: "otherComplicationOfTrauma",
            stateKey: "otherComplicationOfTrauma",
          },
          {
            code: "T90-T98",
            name: "sequelaeOfInjuries",
            stateKey: "sequelaeOfInjuries",
          },
          {
            code: "U78-U88",
            name: "chronicCodes",
            stateKey: "chronicCodes",
          },
        ];

        let totalCodes = 0;
        let parentCount = 0;
        let childCount = 0;

        // Process each range
        ranges.forEach((range, rangeIndex) => {
          try {
            // console.group(
            //   ` Processing ${range.name} (${range.code}) - ${rangeIndex + 1}/${
            //     ranges.length
            //   }`
            // );

            const rangeParts = range.code.split(",");
            let allCodes = [];

            rangeParts.forEach((part, partIndex) => {
              const rangeCodes = getAllCodesFromRange(part, selectedCategory);

              if (rangeCodes && rangeCodes.length > 0) {
                allCodes = [...allCodes, ...rangeCodes];
              } else {
                // console.log(`No codes found in this part`);
              }
            });

            // Count parents and children
            const parentsInRange = allCodes.filter(
              (code) => code._isParentNode
            ).length;
            const childrenInRange = allCodes.filter(
              (code) => !code._isParentNode
            ).length;

            parentCount += parentsInRange;
            childCount += childrenInRange;

            // Store in Redux
            reduxDispatch(
              setRangeChildren({
                rangeType: range.stateKey,
                children: allCodes,
              })
            );

            totalCodes += allCodes.length;
          } catch (error) {
            console.groupEnd();
          }
        });
      },
      [reduxDispatch, getAllCodesFromRange]
    );
    // Fetch all range individual codes when component mounts and tabular data is available
    useEffect(() => {
      const fetchRanges = () => {
        // Check if tabular data is available
        const hasTabularData = icd10amTabularData && pcsTabularData;

        if (hasTabularData) {
          fetchAndStoreRangeChildren(filterTabularCodes, "Principal Diagnosis");
        }
      };

      // Use a small delay to ensure everything is loaded
      const timer = setTimeout(fetchRanges, 1000);
      return () => clearTimeout(timer);
    }, [
      icd10amTabularData,
      pcsTabularData,
      fetchAndStoreRangeChildren,
      filterTabularCodes,
    ]);

    const filterTabularCodesWithHyphen = useCallback(
      (baseCode, category) => {
        if (category === "Procedure Codes" || category === "PCS Codes") return [];
        const normalizedCode = baseCode.replace(/\./g, "").toLowerCase();
        const tabularData =
          category === "Procedure Codes" || category === "PCS Codes" ?  pcsTabularData : icd10amTabularData;
        return findTabularChildrenWithHyphen(tabularData || [], normalizedCode);
      },
      [icd10amTabularData, pcsTabularData, findTabularChildrenWithHyphen]
    );

    const isHyphenCode = useCallback((code) => {
      if (!code || typeof code !== "string") return false;
      const trimmedCode = code.trim();
      return (
        /-$/.test(trimmedCode) ||
        /\s+-\s+/.test(trimmedCode) ||
        /-\*$/.test(trimmedCode) ||
        /^[A-Za-z]\d+-(\.\d+|\d+)$/i.test(trimmedCode)
      );
    }, []);

    const extractCodesFromHyphen = useCallback(
      (code) => {
        if (!isHyphenCode(code)) return [code];
        const trimmedCode = code.trim();
        let codes = [];

        const patternMatch = trimmedCode.match(/^([A-Za-z]\d+)-(\.\d+|\d+)$/i);
        if (patternMatch) {
          const baseCode = patternMatch[1];
          const endPart = patternMatch[2];
          return { type: "range", start: baseCode, end: endPart };
        }

        if (trimmedCode.includes("+")) {
          const parts = trimmedCode.split("+");
          const firstCode = parts[0].replace(/[-*]$/, "").trim();
          const secondCode = parts[1].replace(/[-*]$/, "").trim();
          codes = [firstCode, secondCode];
        } else if (trimmedCode.includes(" - ")) {
          codes = [trimmedCode.split(" - ")[0].trim()];
        } else {
          codes = [trimmedCode.replace(/[-*]+$/, "").trim()];
        }
        return { type: "list", codes };
      },
      [isHyphenCode]
    );

    const getTabularExtensions = useCallback(
      (baseCode, category, extraOptionToIdentify = null) => {
        const normalizedCode = baseCode?.replace(/\./g, "").toLowerCase();
        const tabularData =
          category === "Procedure Codes"|| category === "PCS Codes" ?  pcsTabularData: icd10amTabularData;
        const extensions = findTabularChildren(
          tabularData || [],
          normalizedCode,
          [],
          { value: false },
          extraOptionToIdentify
        );
        const uniqueExtensions = Array.from(
          new Set(extensions.map((ext) => ext.rawName))
        ).map((name) => extensions.find((ext) => ext.rawName === name));
        return uniqueExtensions.map((ext) => {
          return {
            rawName: ext.rawName,
            rawDescription: ext.rawDescription || ext.name,
            description: ext.rawDescription || ext.name,
            children: ext.children || [],
            isFromTabular: true,
            id: ext.id,
            acs: ext.acs,
            code_first_instruction: ext.code_first_instruction,
            use_additional_instruction: ext.use_additional_instruction,
            codealsowhenperf_instruction: ext.codealsowhenperf_instruction,
          };
        });
      },
      [icd10amTabularData, pcsTabularData, findTabularChildren]
    );

    const processHyphenCode = useCallback(
      (selectedCode, category) => {
        if (category === "Procedure Codes") return null;

        const codeToCheck = (
          selectedCode.rawDescription ||
          selectedCode.description ||
          ""
        ).trim();
        if (!codeToCheck || !isHyphenCode(codeToCheck)) return null;

        const extracted = extractCodesFromHyphen(codeToCheck);

        let newFilteredCodes = [];
        if (extracted.type === "range") {
          const tabularResults = filterTabularCodesWithHyphen(
            extracted.start,
            category
          ).filter((item) => item.rawName?.endsWith(extracted.end));
          newFilteredCodes = tabularResults;
        } else {
          const primaryCode = extracted.codes[0];
          const tabularResults = filterTabularCodesWithHyphen(
            primaryCode,
            category
          );
          newFilteredCodes = tabularResults;
        }

        const indexChildren = (
          selectedCode.subcodes ||
          selectedCode.children ||
          []
        )
          .filter((child) => child.name?.trim())
          .map((child) => ({
            rawName: child.name,
            rawDescription: child.description,
            name: child.name,
            description: child.description,
            children: child.subcodes || child.children || [],
            isFromTabular: false,
            acs: child.acs,
            id: child.id,
            code_first_instruction: child.code_first_instruction,
            use_additional_instruction: child.use_additional_instruction,
            codealsowhenperf_instruction: child.codealsowhenperf_instruction,
          }));

        const filteredTabularResults = newFilteredCodes.filter(
          (item) => item.rawName?.trim() && item.rawDescription?.trim()
        );

        const seenNames = new Set();
        const finalCodes = [...indexChildren, ...filteredTabularResults].filter(
          (item) => {
            if (seenNames.has(item.name)) return false;
            seenNames.add(item.name);
            return true;
          }
        );

        return {
          filteredCodes: finalCodes,
          parent: selectedCode,
        };
      },
      [isHyphenCode, extractCodesFromHyphen, filterTabularCodesWithHyphen]
    );

    const buildHistoryNode = useCallback(
      (code, isParent = false) => {
        return {
          category: selectedCategory,
          code: code.rawName || code.rawDescription || "",
          description: isParent
            ? ""
            : code.rawDescription || code.rawName || "",
          isFromTabular: code.isFromTabular || false,
          timestamp: new Date().toISOString(),
          children: [],
          id: code.id,
        };
      },
      [selectedCategory]
    );

    const isCodeInHistory = (code, description, history) => {
      return history.some((entry) => {
        if (entry.code === code && entry.description === description)
          return true;
        if (entry.children) {
          return entry.children.some(
            (child) =>
              (child.code === code && child.description === description) ||
              (child.children &&
                child.children.some(
                  (grandchild) =>
                    grandchild.code === code &&
                    grandchild.description === description
                ))
          );
        }
        return false;
      });
    };

    useEffect(() => {
      reduxDispatch(resetLocalState());

      const principalDiagnosisArray =
        codingscreendata?.data?.principal_diagnosis || [];
      const procedureDiagnosisArray= codingscreendata?.data?.procedure_codes || [];
      const hasValidDiagnosis = principalDiagnosisArray.some(
        (item) =>
          (item?.code && item.code.trim()) ||
          (item?.description && item.description.trim())
      );
      const hasValidProcedure = procedureDiagnosisArray.some(
        (item) =>
          (item?.code && item.code.trim()) ||
          (item?.description && item.description.trim())
      );
      let initialCategory;

if (typeOfPatient === "outPatient") {
  initialCategory = "CPT Tabular";
} else {
  initialCategory = hasValidDiagnosis 
    ? "Additional Diagnoses"
    : "Principal Diagnosis";
}

      reduxDispatch(setSelectedCategory(initialCategory));

      window.history.replaceState(
        { ...localState, selectedCategory: initialCategory },
        "",
        window.location.pathname
      );
    }, [codingscreendata, reduxDispatch]);

    const [debouncedSearchTerm] = useDebounce(searchTerm, 100);
    useEffect(() => {
      if (!initialSearchDone && searchTerm.length >= 3) {
        const filtered = filterCodes(
          debouncedSearchTerm,
          dataSource,
          initialSearchDone,
          selectedCategory
        );
        reduxDispatch(setFilteredCodes(filtered));
      }
    }, [
      debouncedSearchTerm,
      dataSource,
      initialSearchDone,
      selectedCategory,
      filterCodes,
      reduxDispatch,
    ]);

    useEffect(() => {
      const handlePopState = (event) => {
        if (event.state) {
          reduxDispatch(updateLocalState(event.state));
        }
      };
      window.addEventListener("popstate", handlePopState);
      return () => window.removeEventListener("popstate", handlePopState);
    }, [reduxDispatch]);

    const pushHistoryState = (newState) => {
      const stateToPush = {
        ...newState,
        searchTerm: newState.searchTerm,
        selectedIndex: newState.selectedIndex,
        filteredCodes: newState.filteredCodes,
        selectedCategory: newState.selectedCategory,
        currentCodeSearchData: newState.currentCodeSearchData,
        showFinalScreen: newState.showFinalScreen || false,
        selectedFinalCode: newState.selectedFinalCode,
        initialSearchDone: newState.initialSearchDone,
        selectedCodes: newState.selectedCodes,
        selectedParent: newState.selectedParent,
        topLevelParent: newState.topLevelParent,
        lastAddedCode: newState.lastAddedCode,
        originalCode: newState.originalCode,
        currentPath: newState.currentPath,
        pendingInstructions: newState.pendingInstructions || [],
        currentInstructionIndex: newState.currentInstructionIndex || 0,
        currentWarningMessage: newState.currentWarningMessage || null,
        specialCodesToProcess: newState.specialCodesToProcess,
        currentSpecialCode: newState.currentSpecialCode,
        nextRangeToProcess: newState.nextRangeToProcess || null,
      };
      window.history.pushState(stateToPush, "", window.location.pathname);
    };

    const parseSeeTerm = (term) => {
      if (!term || typeof term !== "string" || term.trim() === "") {
        return [];
      }

      const cleanedTerm = term
        .replace(/^[—–\-→]+/g, "") // remove any leading dash, arrow, etc.
        .replace(/[\uFFFD]?\s*(?:see\s+also|see)\s*/gi, "")
        .replace(/^\(|\)$/g, "")
        .replace(/\s*[/→–\-]+\s*/g, "/") // normalize all separators (comma, arrow, dash) to '/'
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase()
        .split("/")
        .map((part) => part.trim())
        .filter((part) => part.length > 0);

      return cleanedTerm;
    };

    const resolveSeePath = (pathParts, category) => {
      if (!pathParts.length) {
        return null;
      }

      const indexData =
        category === "Procedure Codes" ? pcsIndexData : icd10amIndexData;
      if (!indexData || !Array.isArray(indexData)) {
        return null;
      }

      const normalize = (str) =>
        str?.toLowerCase().replace(/\s+/g, " ").trim() || "";

      let currentLevel = indexData;
      let foundItem = null;

      for (let i = 0; i < pathParts.length; i++) {
        const targetPart = normalize(pathParts[i]);

        foundItem = currentLevel.find(
          (item) =>
            normalize(item.name) === targetPart ||
            normalize(item.name).startsWith(targetPart)
        );

        if (!foundItem) {
          return null;
        }

        if (i === pathParts.length - 1) {
          const itemChildren = foundItem.children || foundItem.subcodes || [];
          let results = [];

          if (itemChildren.length > 0) {
            results = itemChildren.map((child) => ({
              rawName: child.name,
              rawDescription:
                child.description ||
                (child.description === null ? null : child.name),
              name: child.name,
              description:
                child.description ||
                (child.description === null ? null : child.name),
              children: child.subcodes || child.children || [],
              isFromTabular: false,
              Next_parent_code: child.Next_parent_code,
              Warning_message: child.Warning_message,
              see: child.see,
              seealso: child.seealso,
              isDescriptionNull: child.description === null,
              acs: child.acs,
              code_first_instruction: child.code_first_instruction,
              use_additional_instruction: child.use_additional_instruction,
              codealsowhenperf_instruction: child.codealsowhenperf_instruction,
              id: child.id,
              seeparentId: foundItem.id,
              seeparentName: foundItem.name,
            }));
          } else {
            results.push({
              rawName: foundItem.name,
              rawDescription:
                foundItem.description ||
                (foundItem.description === null ? null : foundItem.name),
              name: foundItem.name,
              description:
                foundItem.description ||
                (foundItem.description === null ? null : foundItem.name),
              children: [],
              isFromTabular: false,
              isDescriptionNull: foundItem.description === null,
              acs: foundItem.acs,
              code_first_instruction: foundItem.code_first_instruction,
              use_additional_instruction: foundItem.use_additional_instruction,
              codealsowhenperf_instruction:
                foundItem.codealsowhenperf_instruction,
              id: foundItem.id,
              see: foundItem.see,
              seealso: foundItem.seealso,
            });
          }

          return results.length > 0 ? results : null;
        } else {
          currentLevel = foundItem.children || foundItem.subcodes || [];
          if (!currentLevel || currentLevel.length === 0) {
            return null;
          }
        }
      }

      return null;
    };
    const isCodeInInjuryTracker = (code) => {
      // if(code.children && code.children.length>0){
      //   return
      // }
      if (!injuryCodeFlowTracker || !Array.isArray(injuryCodeFlowTracker))
        return false;

      const isTracked = injuryCodeFlowTracker.some((trackedCode) => {
        const idMatch = trackedCode.id === code.id;
        const nameMatch = trackedCode.rawName === code.rawName;
        const descMatch = trackedCode.rawDescription === code.rawDescription;
        const tabularMatch = trackedCode.rawDescription === code.rawName;

        if (idMatch || (nameMatch && descMatch) || tabularMatch) {
          return true;
        }
        return false;
      });

      return isTracked;
    };
    const findTabularChildrenForMultipleRanges = (
      items,
      rangeCodes,
      category,
      results = []
    ) => {
      if (!rangeCodes || !Array.isArray(rangeCodes)) return results;

      const normalizeCode = (code) =>
        code?.replace(/\./g, "").toUpperCase().trim();

      // Process each range code
      rangeCodes.forEach((rangeCode) => {
        const isRange = rangeCode.includes("-");
        let rangeStart, rangeEnd;

        if (isRange) {
          const parts = rangeCode.split("-").map((part) => normalizeCode(part));
          [rangeStart, rangeEnd] = parts;
        } else {
          // Single code
          rangeStart = normalizeCode(rangeCode);
          rangeEnd = null;
        }

        const findRangeCodes = (items) => {
          for (const item of items) {
            // Handle chapters
            if (item.chapter && item.codes) {
              findRangeCodes(item.codes);
              continue;
            }

            const itemCode = item.name;
            if (!itemCode) continue;

            const itemCodeNormalized = normalizeCode(itemCode);

            // Check if this item exactly matches our range or single code
            const matchesRange = isRange
              ? rangeEnd
                ? compareCodes(itemCodeNormalized, rangeStart, rangeEnd)
                : itemCodeNormalized.startsWith(rangeStart)
              : itemCodeNormalized === rangeStart;

            if (matchesRange) {
              // Add the range code itself
              results.push({
                rawName: item.name, // This will be the range code like "V00-X59"
                rawDescription: item.description || item.name,
                name: item.name,
                description: item.description || item.name,
                children: item.subcodes || item.children || [],
                isFromTabular: true,
                id: item.id,
                acs: item.acs,
                code_first_instruction: item.code_first_instruction,
                use_additional_instruction: item.use_additional_instruction,
                codealsowhenperf_instruction: item.codealsowhenperf_instruction,
                _isRangeCode: true,
                _originalRange: rangeCode,
              });

              // Don't search deeper - we found our exact range
              continue;
            }

            // Continue searching in nested structures
            [item.codes, item.subcodes, item.children]
              .filter(Boolean)
              .forEach((nested) => findRangeCodes(nested));
          }
        };

        findRangeCodes(items);
      });

      // Remove duplicates based on rawName
      const uniqueResults = results.filter(
        (result, index, self) =>
          index === self.findIndex((r) => r.rawName === result.rawName)
      );

      return uniqueResults;
    };
    // Helper function to check if a code is already in injuryCodeFlowTracker

    const filterTabularCodesForMultipleRanges = (
      rangeCodesString,
      category
    ) => {
      if (category === "Procedure Codes") return [];

      // Split the range string by commas and trim each part
      const rangeCodes = rangeCodesString.split(",").map((code) => code.trim());

      const tabularData =
        category === "Procedure Codes" ? achiTabularData : icd10amTabularData;

      const allResults = findTabularChildrenForMultipleRanges(
        tabularData || [],
        rangeCodes,
        category
      );

      // Filter out codes that are already in injuryCodeFlowTracker
      const filteredResults = allResults.filter((code) => code);
      return filteredResults;
    };
    // remove chnapter logic Table of drugs and chemicals
    const removeChapter19AndStore = (list) => {
      return list.map((item) => {
        const ch19 = item.children?.find((c) =>
          c.name?.toLowerCase().includes("chapter 19")
        );

        return {
          ...item,
          children: item.children
            ? item.children.filter(
                (c) => !c.name?.toLowerCase().includes("chapter 19")
              )
            : [],
          chapter19Info: ch19
            ? {
                rawName: ch19.rawName || ch19.name,
                rawDescription: ch19.description || ch19.name,
                id: ch19.id,
              }
            : null,
        };
      });
    };

    const handleContinue = useCallback(() => {
      let newState = {
        searchTerm,
        selectedIndex,
        filteredCodes,
        currentCodeSearchData,
        showFinalScreen,
        selectedFinalCode,
        selectedCategory,
        initialSearchDone,
        selectedCodes: { ...localSelectedCodes },
        selectedParent,
        topLevelParent,
        lastAddedCode,
        showError: false,
        originalCode,
        currentPath,
        pendingInstructions: localState.pendingInstructions || [],
        currentInstructionIndex: localState.currentInstructionIndex || 0,
        currentWarningMessage: localState.currentWarningMessage || null,
        specialCodesToProcess: [...localState.specialCodesToProcess],
        currentSpecialCode: localState.currentSpecialCode,
        nextRangeToProcess: localState.nextRangeToProcess || null,
        indexToTabularExtensions:localState.indexToTabularExtensions||[],

      };

      if (selectedIndex !== null && filteredCodes?.[selectedIndex]) {
        const selectedCode = filteredCodes[selectedIndex];
        console.log(selectedCode, "seddd", localState);

        const hasInstructions =
          (Array.isArray(selectedCode.code_first_instruction) &&
            selectedCode.code_first_instruction.length > 0) ||
          (Array.isArray(selectedCode.use_additional_instruction) &&
            selectedCode.use_additional_instruction.length > 0) ||
          (Array.isArray(selectedCode.codealsowhenperf_instruction) &&
            selectedCode.codealsowhenperf_instruction.length > 0 &&
            !selectedCode.description !== null &&
            !selectedCode.children.length > 0);

        if (selectedCode._isCustomOption === true) {
          const checkIsFromTabular = localState.lastAddedCode?.isFromTabular;

          if (checkIsFromTabular) {
            setTimeout(() => {
              reduxDispatch(
                setRawSearchQuery(localState.lastAddedCode?.rawDescription)
              );
            }, 0);
            if (selectedCategory === "Procedure Codes") {
              reduxDispatch(setActiveTab("PCS Tabular"));
            } else {
              reduxDispatch(setActiveTab("ICD 10 CM Tabular"));
            }
            reduxDispatch(setActiveIcon("Tabular"));
          } else {
            if (selectedCategory === "Procedure Codes") {
              setTimeout(() => {
                reduxDispatch(
                  setRawSearchQuery(localState.lastAddedCode?.rawName)
                );
              }, 0);
            } else {
              setTimeout(() => {
                reduxDispatch(
                  setRawSearchQuery(localState.lastAddedCode?.name)
                );
              });
            }
          }
          reduxDispatch(setSearchByCodeId(localState.lastAddedCode?.id));
        }

        if (selectedCode.children && selectedCode.children.length === 0) {
          newState.currentWarningMessage = null;
        }

        reduxDispatch(handleStoreParentAndChilds(selectedCode));

        const isAdditionalAndFirstEmpty =
          Array.isArray(selectedCode.use_additional_instruction) &&
          selectedCode.use_additional_instruction === " " &&
          Array.isArray(selectedCode.code_first_instruction) &&
          selectedCode.code_first_instruction === "";

        if (isAdditionalAndFirstEmpty) {
          const finalState = {
            ...localState,
            lastAddedCode: selectedCode,
            showFinalScreen: true,
            showError: false,
          };
          reduxDispatch(updateLocalState(finalState));
          reduxDispatch(clearWarningMessage());
          pushHistoryState(finalState);
          return;
        }
        // 🟣 FIX: use referenceId as id only for See Also options
        const effectiveSelectedCode =
          selectedCode?.isSeeAlsoOption && selectedCode?.referenceId
            ? { ...selectedCode, id: selectedCode.referenceId }
            : selectedCode;
        const newNode = {
          category:
            selectedCode?.children && selectedCode.children.length === 0
              ? selectedCategory
              : null,
          code:
            selectedCode._type === "child"
              ? selectedCode.parentName
              : selectedCode.rawName || selectedCode.rawDescription || "",
          description:
            selectedCode.rawDescription || selectedCode.rawName || "",
          isFromTabular: selectedCode.isFromTabular || false,
          timestamp: new Date().toISOString(),
          id: effectiveSelectedCode.id,
        };

        if (!initialSearchDone && !topLevelParent) {
          newState.topLevelParent = selectedCode;
          newState.originalCode = normalizeCodeObject(selectedCode);
          newState.currentPath = [buildHistoryNode(selectedCode, true)];
        } else {
          newState.currentPath = [...currentPath, newNode];
        }
        reduxDispatch(updateCodePathHistory(newNode));

        if (
           !selectedCode.isDirectCode &&   // KEY FIX
         !selectedCode.isSeeAlsoOption &&
          (!selectedCode.children || selectedCode.children.length === 0) &&
          (!selectedCode.subcodes || selectedCode.subcodes.length === 0) &&
          selectedCode.seealso &&
          selectedCode.seealso.trim()
        ) {
          const seeAlsoTerm = selectedCode.seealso.trim();

          const seeAlsoOption = {
            rawName: `${seeAlsoTerm}`,
            rawDescription: "Navigate to referenced codes",
            isSeeAlsoOption: true,
            option: seeAlsoTerm,
            originalCode: selectedCode,
            acs: selectedCode.acs,
            referenceId: selectedCode.id,
            category: selectedCategory.category,
          };

          // Direct selectable code (final option)
          const directOption = {
             ...selectedCode,
            isDirectCode: true,              // IMPORTANT
            isSeeAlsoUnspecified: true,
          };

          // Combine both options for this case
          const combined = [directOption, seeAlsoOption];

          newState = {
            ...newState,
            selectedParent: selectedCode,
            currentCodeSearchData: combined,
            filteredCodes: combined,
            selectedIndex: null,
            initialSearchDone: true,
            showFinalScreen: false,
            originalCode: normalizeCodeObject(selectedCode),
          };

          reduxDispatch(updateLocalState(newState));
          pushHistoryState(newState);
          return;
        }
  


        const processNextInstruction = async (instructions) => {
          if (!instructions || instructions.length === 0) {
            // All instructions processed - finalize
            const currArr =
              (newState.selectedCodes &&
                newState.selectedCodes[selectedCategory]) ||
              [];
            const codeKey = `${selectedCode.rawName}-${selectedCode.rawDescription}`;
            const isOriginalPresent = currArr.some(
              (c) => `${c.rawName}-${c.rawDescription}` === codeKey
            );

            if (!isOriginalPresent) {
              newState.selectedCodes = {
                ...newState.selectedCodes,
                [selectedCategory]: [
                  ...currArr,
                  {
                    rawName: selectedCode.rawName,
                    rawDescription: selectedCode.rawDescription,
                    isFromTabular: selectedCode?.isFromTabular || false,
                    acs: selectedCode.acs,
                    code_first_instruction: selectedCode.code_first_instruction,
                    use_additional_instruction:
                      selectedCode.use_additional_instruction,
                    codealsowhenperf_instruction:
                      selectedCode.codealsowhenperf_instruction, // Include this
                    id: selectedCode.id,
                  },
                ],
              };
              newState.lastAddedCode = selectedCode;
            }

            newState.showFinalScreen = true;
            newState.pendingInstructions = [];
            newState.currentInstructionIndex = 0;
            newState.currentWarningMessage = null;
            reduxDispatch(updateLocalState(newState));
            pushHistoryState(newState);
            return;
          }

          const [current, ...remaining] = instructions;
          let displayWarning = null;
          newState.pendingInstructions = remaining;
          newState.currentWarningMessage = current.Warning_message || null;
          if (current._instructionType === "codealsowhenperf") {
            displayWarning = `Code also when performed: ${current.Warning_message}`;
            newState.currentWarningMessage = displayWarning || null;
          }

          if (current.Warning_message) {
            if (current._instructionType === "codealsowhenperf") {
              displayWarning = `Code also when performed: ${current.Warning_message}`;
              reduxDispatch(setCurrentWarningMessage(displayWarning));
            } else {
              reduxDispatch(setCurrentWarningMessage(current.Warning_message));
            }
          } else {
            reduxDispatch(clearWarningMessage());
          }

          // Process Next_Parent_code for ALL instruction types including codealsowhenperf
          let extensions = [];
          if (
            current.Next_Parent_code &&
            current.Next_Parent_code.toString().trim() !== ""
          ) {
            const result = await filterTabularCodes(
              current.Next_Parent_code,
              selectedCategory
            );
            console.log(
              "Extensions found for",
              current._instructionType,
              ":",
              result
            );
            extensions = Array.isArray(result) ? result : [];
          } else if (
            Array.isArray(current.children) &&
            current.children.length > 0
          ) {
            // Handle children for all instruction types
            const noOption = {
              rawName: "No or already coded",
              rawDescription: "No or already coded",
              name: "No or already coded",
              description: "No or already coded",
              children: [],
              isFromTabular: false,
              _isCustomOption: true,
            };

            const mapChildrenToOptions = (childrenArr = []) =>
              (childrenArr || [])
                .filter((child) => child && (child.name || child.rawName))
                .map((child) => ({
                  rawName: child.name || child.rawName,
                  rawDescription:
                    child.description ??
                    (child.description === null
                      ? null
                      : child.name || child.rawName),
                  name: child.name || child.rawName,
                  description:
                    child.description ??
                    (child.description === null
                      ? null
                      : child.name || child.rawName),
                  children: child.subcodes || child.children || [],
                  isFromTabular:
                    child.isFromTabular || selectedCode.isFromTabular || false,
                  Next_parent_code:
                    child.Next_parent_code || child.Next_Parent_code || null,
                  Warning_message: child.Warning_message,
                  see: child.see,
                  seealso: child.seealso,
                  isDescriptionNull: child.description === null,
                  id: child.id,
                  acs: child.acs,
                  code_first_instruction: child.code_first_instruction,
                  use_additional_instruction: child.use_additional_instruction,
                  codealsowhenperf_instruction:
                    child.codealsowhenperf_instruction, // Include 
                    index_to_tabular_instruction:child.index_to_tabular_instruction,
                }));

            const childrenOpts = mapChildrenToOptions(current.children);
            newState.currentCodeSearchData = childrenOpts;
            newState.filteredCodes = [noOption, ...childrenOpts];
            newState.selectedIndex = null;
            newState.initialSearchDone = true;
            newState.showFinalScreen = false;
            newState.originalCode = normalizeCodeObject(selectedCode);

            reduxDispatch(updateLocalState(newState));
            pushHistoryState(newState);
            return;
          }

          console.log(
            "Extensions for",
            current._instructionType,
            ":",
            extensions
          );

          if (extensions.length > 0) {
            // Pause for user selection (extensions) - this should work for codealsowhenperf too
            const noOption = {
              rawName: "No or already coded",
              rawDescription: "No or already coded",
              name: "No or already coded",
              description: "No or already coded",
              children: [],
              isFromTabular: false,
              _isCustomOption: true,
            };

            newState.currentCodeSearchData = extensions;
            newState.filteredCodes = [noOption, ...extensions];
            newState.selectedIndex = null;
            newState.initialSearchDone = true;
            newState.showFinalScreen = false;
            newState.originalCode = normalizeCodeObject(selectedCode);

            reduxDispatch(updateLocalState(newState));
            pushHistoryState(newState);
            return;
          } else {
            // No Next_Parent_code / no extensions -> continue to next instruction
            const shouldProcessSpecialCodesAfterInstructions = () => {
              const hasPendingSpecialCodes =
                localState.specialCodesToProcess &&
                localState.specialCodesToProcess.length > 0;
              const hasMoreInstructions = remaining && remaining.length > 0;
              return hasPendingSpecialCodes && !hasMoreInstructions;
            };

            if (shouldProcessSpecialCodesAfterInstructions()) {
              processSpecialCodesAfterInstructions(remaining);
            } else {
              processNextInstruction(remaining);
            }
          }
        };

        if (selectedCode._isCustomOption === true) {
          // ENHANCED: Handle special codes case
          const isInSpecialCodeFlow = localState.processingSpecialCodes;

          if (isInSpecialCodeFlow) {
            // Use the lastAddedCode from special code processing
            const codeToUseForSearch = localState.lastAddedCode;

            if (codeToUseForSearch) {
              // Set search query based on the last processed special code
              // For tabular items, use description; for index items, use name/code
              let searchTerm = "";

              if (codeToUseForSearch.isFromTabular) {
                searchTerm =
                  codeToUseForSearch.rawDescription ||
                  codeToUseForSearch.description ||
                  "";
              } else {
                searchTerm =
                  codeToUseForSearch.rawName ||
                  codeToUseForSearch.name ||
                  codeToUseForSearch.code ||
                  "";
              }

              if (searchTerm) {
                console.log('check4');
                reduxDispatch(setRawSearchQuery(searchTerm));
              }

              if (codeToUseForSearch.id) {
                reduxDispatch(setSearchByCodeId(codeToUseForSearch.id));
              }
            } else {
              console.log("No codeToUseForSearch found");
            }
          } else {
            // Existing instruction flow logic
            if (localState.lastAddedCode) {
              console.log('check5');
              reduxDispatch(
                setRawSearchQuery(
                  localState.lastAddedCode.name ||
                    localState.lastAddedCode.rawName
                )
              );
              reduxDispatch(setSearchByCodeId(localState.lastAddedCode.id));
            }
          }

          // Check if we have more instructions or special codes to process
          const hasMoreInstructions =
            newState.pendingInstructions &&
            newState.pendingInstructions.length > 0;

          if (hasMoreInstructions) {
            processNextInstruction(newState.pendingInstructions);
            return;
          } else {
            // console.log(
            //   "✅ No more instructions or special codes - showing final screen"
            // );

            // Finalize - no more instructions or special codes
            const finalState = {
              ...localState,
              showFinalScreen: true,
              pendingInstructions: [],
              currentInstructionIndex: 0,
              processingSpecialCodes: false,
              specialCodesToProcess: [],
              selectedParent: selectedCode,
              lastAddedCode: selectedCode,
            };
            reduxDispatch(updateLocalState(finalState));
            pushHistoryState(finalState);
            return;
          }
        }

        // Check if the selected code has children to show
        const hasChildrenToShow =
          (selectedCode.children && selectedCode.children.length > 0) ||
          (selectedCode.subcodes && selectedCode.subcodes.length > 0);
        // Add this check at the beginning of handleContinue or in a useEffect
        if (hasInstructions && !hasChildrenToShow) {
          // gather both instruction arrays
          const firstInstr = selectedCode.code_first_instruction || [];

          const addInstr = selectedCode.use_additional_instruction || [];

          const addCodeWhen = selectedCode.codealsowhenperf_instruction || [];

          const allInstrs = [...firstInstr, ...addInstr, ...addCodeWhen];

          // determine if there's at least one actionable instruction:
          // - a non-empty Next_Parent_code OR instruction with children
          const hasValidNextParentCode = allInstrs.some(
            (instr) =>
              instr &&
              instr.Next_Parent_code &&
              instr.Next_Parent_code.toString().trim() !== ""
          );

          const hasChildrenInstr = allInstrs.some(
            (instr) =>
              instr &&
              Array.isArray(instr.children) &&
              instr.children.length > 0
          );
          const shouldProcessInstructions =
            hasValidNextParentCode || hasChildrenInstr;

          // if no actionable instruction, fall through to normal child/final processing
          if (!shouldProcessInstructions) {
            /* do nothing here — allow the code below (children / final processing)
       to run as if there were no instructions */
          } else {
            console.log(
              shouldProcessInstructions,
              "shouldProcessInstructionsgoing"
            );

            // Ensure original code is added first (if not already present)
            const existingArr = localSelectedCodes[selectedCategory] || [];
            const codeKey = `${selectedCode.rawName}-${selectedCode.rawDescription}`;
            const alreadyAdded = existingArr.some(
              (c) => `${c.rawName}-${c.rawDescription}` === codeKey
            );

            if (!alreadyAdded) {
              // Skip adding "No or already coded" pseudo-option
              if (
                selectedCode.rawName === "No or already coded" ||
                selectedCode.rawDescription === "No or already coded"
              ) {
                return; // simply exit early — don’t add it
              }

              const newArr = [
                ...existingArr,
                {
                  rawName: selectedCode.rawName,
                  rawDescription: selectedCode.rawDescription,
                  isFromTabular: selectedCode.isFromTabular || false,
                  acs: selectedCode.acs,
                  code_first_instruction: selectedCode.code_first_instruction,
                  use_additional_instruction:
                    selectedCode.use_additional_instruction,
                  codealsowhenperf_instruction:
                    selectedCode.codealsowhenperf_instruction,
                  lastAddedCode: selectedCode,
                  id: selectedCode.id,
                },
              ];

              newState = {
                ...newState,
                selectedCodes: {
                  ...localSelectedCodes,
                  [selectedCategory]: newArr,
                },
                lastAddedCode: selectedCode,
                selectedParent: selectedCode,
              };

              reduxDispatch(updateLocalState(newState));
            }

            (async () => {
              // Flatten instructions: produce items with Next_Parent_code normalized.
              const flattenInstructions = (
                instructions,
                isAdditional = false,
                instructionType = "code_first"
              ) =>
                (instructions || []).flatMap((instr) => {
                  const next = instr?.Next_Parent_code;

                  const codes =
                    next && next.toString().trim()
                      ? next
                          .toString()
                          .split(",")
                          .map((c) => c.trim())
                          .filter(Boolean)
                      : [];

                  // If no Next_Parent_code but children exist, keep the instr (with Next_Parent_code: null)
                  if (codes.length === 0) {
                    return [
                      {
                        ...instr,
                        Next_Parent_code: null,
                        _parent: {
                          rawName: selectedCode.rawName,
                          rawDescription: selectedCode.rawDescription,
                          id: selectedCode.id,
                          isFromTabular: selectedCode.isFromTabular || false,
                        },
                        _isAdditional: isAdditional,
                        _instructionType: instructionType,
                      },
                    ];
                  }

                  // For comma-separated codes, create individual instructions for EACH code
                  return codes.map((code) => ({
                    ...instr,
                    Next_Parent_code: code, // Single code, not comma-separated
                    _parent: {
                      rawName: selectedCode.rawName,
                      rawDescription: selectedCode.rawDescription,
                      id: selectedCode.id,
                      isFromTabular: selectedCode.isFromTabular || false,
                    },
                    _isAdditional: isAdditional,
                    _instructionType: instructionType,
                  }));
                });

              // Update the pendingInstructions creation:
              const pendingInstructions = [
                ...flattenInstructions(
                  selectedCode.code_first_instruction || [],
                  false,
                  "code_first"
                ),
                ...flattenInstructions(
                  selectedCode.use_additional_instruction || [],
                  true,
                  "use_additional"
                ),
                ...flattenInstructions(
                  selectedCode.codealsowhenperf_instruction || [],
                  false,
                  "codealsowhenperf"
                ), // ADD THIS
              ];

              // Early exit if nothing to process
              if (pendingInstructions.length === 0) return;
              const updatedTopLevelParent = {
                ...selectedParent, // Keep all existing properties
                rawDescription: selectedCode.isFromTabular
                  ? selectedCode.rawDescription
                  : selectedCode.rawName, // Update with selected code's description
                // You might also want to update other relevant fields
                description: selectedCode.isFromTabular
                  ? selectedCode.rawDescription
                  : selectedCode.rawName,
                rawName: selectedCode.isFromTabular
                  ? selectedCode.rawDescription
                  : selectedCode.rawName,
              };

              // UPDATE: Set the modified topLevelParent
              newState = {
                ...newState,
                selectedParent: updatedTopLevelParent,
                originalCode: normalizeCodeObject(selectedCode),
                selectedParent: selectedCode,
              };
              // Add this function inside the async instruction processing

              // helper that maps children (instruction-child objects) into UI items

              // Recursive processor

              // Update state with pending instructions before starting
              newState.pendingInstructions = pendingInstructions;
              newState.currentInstructionIndex = 0;
              newState.showFinalScreen = false;

              reduxDispatch(setPendingInstructions(pendingInstructions));
              reduxDispatch(
                updateLocalState({
                  currentInstructionIndex: 0,
                  showFinalScreen: false,
                })
              );

              processNextInstruction(pendingInstructions);
            })();

            // Return early to prevent regular processing while instructions are running
            return;
          }
        }

        //----------------------------------------------------------------------------------------------------------------------
        const hyphenResult = processHyphenCode(selectedCode, selectedCategory);
        if (hyphenResult) {
          newState = {
            ...newState,
            selectedParent: hyphenResult.parent,
            currentCodeSearchData: hyphenResult.filteredCodes,
            filteredCodes: hyphenResult.filteredCodes,
            selectedIndex: null,
            initialSearchDone: true,
            showFinalScreen: false,
            lastAddedCode: selectedCode,
            originalCode: normalizeCodeObject(selectedCode),
          };
          reduxDispatch(updateLocalState(newState));
          reduxDispatch(clearWarningMessage());
          pushHistoryState(newState);
          return;
        }

        // ✅ INSERT SEE LOGIC HERE — before final screen check
        // -----------------------------------------------------------------
        // ✅ PRIORITY: SEE always takes precedence over description
        if (
          selectedCode &&
          selectedCode.see &&
          selectedCode.see.trim() &&
          selectedCode._type !== "child"
        ) {
          const term = selectedCode.see.trim();
          const originalCode = selectedCode || {};
          const pathParts = parseSeeTerm(term);
          if (
            term.toLowerCase() === "table of drugs and chemicals" ||
            pathParts.join(" ").toLowerCase() === "table of drugs and chemicals"
          ) {
            let children = resolveSeePath(
              ["Table of drugs and chemicals"],
              selectedCategory
            );

            if (children?.length > 0) {
              const updatedChildren = removeChapter19AndStore(children);
              // ⭐ ALSO CHECK AFTER removeChapter19AndStore

              newState = {
                ...newState,
                selectedParent: originalCode,
                currentCodeSearchData: updatedChildren,
                filteredCodes: updatedChildren,
                selectedIndex: null,
                initialSearchDone: true,
                showFinalScreen: false,
                originalCode: normalizeCodeObject(originalCode),
              };

              reduxDispatch(updateLocalState(newState));
              pushHistoryState(newState);
              return;
            }
          }

          const resolveResult = resolveSeePath(
            pathParts,
            newState.selectedCategory
          );

          // ✅ If SEE path resolves successfully → proceed with that
          if (resolveResult && resolveResult.length > 0) {
            newState = {
              ...newState,
              selectedParent: originalCode,
              currentCodeSearchData: resolveResult,
              filteredCodes: resolveResult,
              selectedIndex: null,
              initialSearchDone: true,
              showFinalScreen: false,
              originalCode: normalizeCodeObject(originalCode),
            };
            reduxDispatch(updateLocalState(newState));
            pushHistoryState(newState);
            return; // ✅ stop here, don’t check description or go final
          }

          // ❌ If SEE path fails → show error but still don’t use description
          newState = {
            ...newState,
            lastAddedCode: originalCode,
            showFinalScreen: true,
            showError: true,
          };
          reduxDispatch(updateLocalState(newState));
          pushHistoryState(newState);
          setTimeout(() => {
            reduxDispatch(
              updateLocalState({ showError: false, lastAddedCode: null })
            );
          }, 3000);
          return;
        }
        // -----------------------------------------------------------------
        // Continue with normal processing if no special codes
        // ... rest of your existing normal processing logic
        // If no instructions, process children normally
        if (
          (selectedCode.children && selectedCode.children.length > 0) ||
          (selectedCode.subcodes && selectedCode.subcodes.length > 0)
        ) {
          const children = selectedCode.subcodes || selectedCode.children || [];
          const validChildren = children
            .filter((child) => child && child.name?.trim())
            .map((child) => ({
              rawName: child.name,
              rawDescription:
                child.description ||
                (child.description === null ? null : child.name),
              name: child.name,
              description:
                child.description ||
                (child.description === null ? null : child.name),
              children: child.subcodes || child.children || [],
              isFromTabular: selectedCode.isFromTabular || false,
              Next_parent_code: child.Next_parent_code,
              Warning_message: child.Warning_message,
              see: child.see,
              seealso: child.seealso,
              isDescriptionNull: child.description === null,
              id: child.id,
              acs: child.acs,
              code_first_instruction:
                selectedCode.isFromTabular && child.children
                  ? null
                  : child.code_first_instruction,
              use_additional_instruction:
                selectedCode.isFromTabular && child.children
                  ? null
                  : child.use_additional_instruction,
              codealsowhenperf_instruction: child.codealsowhenperf_instruction,
              index_to_tabular_instruction:child.index_to_tabular_instruction,
            }));

          let newFilteredCodes = [...validChildren];
          let canAddUnspecified = false;
          let unspecifiedCode = null;
          if (selectedCode._type === "child") {
            // Parent info is already attached in child object
            const parentName = selectedCode.parentName;
            const parentDescription = selectedCode.parentDescription;
            const parentChildren = selectedCode.children || []; // <-- children of the parent
            // Only add unspecified if parent has valid name, description, and children
            if (
              parentName?.trim() &&
              parentDescription?.trim() &&
              parentChildren.length > 0 &&
              !selectedCode.isFromTabular
            ) {
              canAddUnspecified = true;
              unspecifiedCode = {
                rawName: parentName,
                rawDescription: parentDescription,
                name: parentDescription || parentName,
                description: parentDescription,
                children: [], // unspecified option has no children
                isUnspecified: true,
                isFromTabular: selectedCode.isFromTabular || false,
                Next_parent_code: selectedCode.Next_parent_code,
                Warning_message: selectedCode.Warning_message,
                id: selectedCode.parentId,
                acs: selectedCode.parentAcs,
                code_first_instruction: selectedCode.code_first_instruction,
                use_additional_instruction:
                  selectedCode.use_additional_instruction,
                codealsowhenperf_instruction:
                  selectedCode.codealsowhenperf_instruction,
                  index_to_tabular_instruction:selectedCode.index_to_tabular_instruction,
              };
            }
          } else {
            // Parent selection: keep existing logic
            canAddUnspecified =
              selectedCode.rawName?.trim() &&
              selectedCode.description?.trim() &&
              selectedCode.description !== null &&
              !selectedCode.isFromTabular;
            if (canAddUnspecified) {
              unspecifiedCode = {
                rawName: selectedCode.rawName,
                rawDescription: selectedCode.description,
                name: selectedCode.description || selectedCode.rawName,
                description: selectedCode.description,
                children: [],
                isUnspecified: true,
                isFromTabular: selectedCode.isFromTabular || false,
                Next_parent_code: selectedCode.Next_parent_code,
                Warning_message: selectedCode.Warning_message,
                id: selectedCode.id,
                acs: selectedCode.acs,
                code_first_instruction: selectedCode.code_first_instruction,
                use_additional_instruction:
                  selectedCode.use_additional_instruction,
                codealsowhenperf_instruction:
                  selectedCode.codealsowhenperf_instruction,
                  index_to_tabular_instruction:selectedCode.index_to_tabular_instruction,
              };
            }
          }
          //see and see also condition
          // Push to filtered options if valid

          // --- SEE ALSO: preserve unspecified & children — append appropriately ---
          const hasSeeAlso =
            selectedCode.seealso && selectedCode.seealso.trim();

          if (hasSeeAlso) {
            const seeAlsoTerm = selectedCode.seealso.trim();

            const seeAlsoOption = {
              rawName: `${seeAlsoTerm}`,
              rawDescription: "Navigate to referenced codes",
              isSeeAlsoOption: true,
              option: seeAlsoTerm,
              originalCode: selectedCode,
              acs: selectedCode.acs,
              referenceId: selectedCode.id,
              category: selectedCategory.category,
            };

            // If we already have children or unspecified in newFilteredCodes -> just append See Also
            if (newFilteredCodes && newFilteredCodes.length > 0) {
              // avoid duplicate seeAlso
              const alreadyHas = newFilteredCodes.some(
                (c) => c.isSeeAlsoOption && c.option === seeAlsoTerm
              );
              if (!alreadyHas) newFilteredCodes.push(seeAlsoOption);
            } else {
              // No children and no unspecified present.
              // Show both: direct selectable code (so user can go final) AND See Also option.

              const directOption = {
                rawName: selectedCode.rawName,
                rawDescription: selectedCode.description,
                name: selectedCode.description || selectedCode.rawName,
                description: selectedCode.description,
                isDirectCode: true, // indicator for downstream final-screen flow
                acs: selectedCode.acs,
                id: selectedCode.id,
                code_first_instruction: selectedCode.code_first_instruction,
                use_additional_instruction:
                  selectedCode.use_additional_instruction,
                codealsowhenperf_instruction:
                  selectedCode.codealsowhenperf_instruction, // Include this
                  index_to_tabular_instruction:selectedCode.index_to_tabular_instruction,
              };

              newFilteredCodes = [directOption, seeAlsoOption];
            }
          }

          if (
            canAddUnspecified &&
            unspecifiedCode &&
            !selectedCode.isFromTabular
          ) {
            newFilteredCodes.push(unspecifiedCode);
          }

          if (
            !validChildren.length &&
            !canAddUnspecified &&
            newState.pendingInstructions.length === 0
          ) {
            newState = {
              ...newState,
              lastAddedCode: selectedCode,
              showFinalScreen: true,
              showError: true,
              selectedParent: selectedCode,
            };
            reduxDispatch(updateLocalState(newState));
            pushHistoryState(newState);
            setTimeout(() => {
              reduxDispatch(
                updateLocalState({ showError: false, lastAddedCode: null })
              );
            }, 3000);
            return;
          }

          newState = {
            ...newState,
            selectedParent: selectedCode,
            currentCodeSearchData: newFilteredCodes,
            filteredCodes: newFilteredCodes,
            selectedIndex: null,
            initialSearchDone: true,
            showFinalScreen: false,
            originalCode: normalizeCodeObject(selectedCode),
          };

          reduxDispatch(updateLocalState(newState));
          pushHistoryState(newState);
          return;
        }
        // // Handle see/see also
        // ✅ Handle SEE ALSO option clicks
        if (selectedCode && selectedCode.isSeeAlsoOption) {
          const term = selectedCode.option.trim();
          const pathParts = parseSeeTerm(term);

          // ⭐ SPECIAL CASE for SEE ALSO → Table of drugs (same logic as SEE)
          if (
            term.toLowerCase() === "table of drugs and chemicals" ||
            pathParts.join(" ").toLowerCase() === "table of drugs and chemicals"
          ) {
            let children = resolveSeePath(
              ["Table of drugs and chemicals"],
              newState.selectedCategory
            );

            if (children?.length > 0) {
              const updatedChildren = removeChapter19AndStore(children);

              newState = {
                ...newState,
                selectedParent: effectiveSelectedCode.originalCode,
                currentCodeSearchData: updatedChildren,
                filteredCodes: updatedChildren,
                selectedIndex: null,
                initialSearchDone: true,
                showFinalScreen: false,
                originalCode: normalizeCodeObject(
                  effectiveSelectedCode.originalCode
                ),
              };

              reduxDispatch(updateLocalState(newState));
              pushHistoryState(newState);
              return;
            }
          }

          const resolveResult = resolveSeePath(
            pathParts,
            newState.selectedCategory
          );
          if (resolveResult && resolveResult.length > 0) {
            // 🟣 Ensure correct id is preserved for see also

            newState = {
              ...newState,
              selectedParent: effectiveSelectedCode.originalCode, // keep link to parent
              currentCodeSearchData: resolveResult,
              filteredCodes: resolveResult,
              selectedIndex: null,
              initialSearchDone: true,
              showFinalScreen: false,
              originalCode: normalizeCodeObject(
                effectiveSelectedCode.originalCode
              ),
            };

            reduxDispatch(updateLocalState(newState));
            pushHistoryState(newState);
            return; // stop here — handled
          }

          newState = {
            ...newState,
            lastAddedCode: selectedCode.originalCode,
            showFinalScreen: true,
            showError: true,
          };
          reduxDispatch(updateLocalState(newState));
          pushHistoryState(newState);
          setTimeout(() => {
            reduxDispatch(
              updateLocalState({ showError: false, lastAddedCode: null })
            );
          }, 3000);
          return;
        }
              // 🔹 SIMPLE PCS INDEX → INDEX TO TABULAR (ONE BY ONE)
        const indexToTabularInstruction =
          selectedCode.index_to_tabular_instruction?.[0];

        if (
          indexToTabularInstruction &&
          indexToTabularInstruction.Next_Parent_code
        ) {
          const nextParentCodes = indexToTabularInstruction.Next_Parent_code
            .split(",")
            .map(code => code.trim());

          (async () => {
            let allExtensions = [];

            // 🔁 CALL filterTabularCodes ONE BY ONE
            for (const code of nextParentCodes) {
              const extensions = await filterTabularCodes(
                code,
                selectedCategory
              );

              if (extensions?.length) {
                allExtensions.push(...extensions);
              }
            }

            // ✅ SHOW OPTIONS
            if (allExtensions.length > 0) {
              newState.currentCodeSearchData = allExtensions;
              newState.filteredCodes = allExtensions;
              newState.selectedIndex = null;
              newState.initialSearchDone = true;
              newState.showFinalScreen = false;
              newState.originalCode = normalizeCodeObject(selectedCode);

              reduxDispatch(updateLocalState(newState));
              pushHistoryState(newState);
            } else {
              // fallback (rare case)
              const updatedState = {
                ...newState,
                lastAddedCode: selectedCode,
                showFinalScreen: true,
              };

              reduxDispatch(updateLocalState(updatedState));
              pushHistoryState(updatedState);
            }
          })();

          return; // stop default flow
        }

        // ✅ Handle single code that has a 'see also' but no children

        const isSeePresent =
          selectedCode &&
          ((selectedCode.see && selectedCode.see.trim() !== "") ||
            (selectedCode.seeAlso && selectedCode.seeAlso.trim() !== ""));

        // If no children, continue with final processing
        const hasNullValueForFinalSelection =
          !isSeePresent &&
          (!selectedCode.rawName?.trim() ||
            selectedCode.description === null ||
            !selectedCode.description?.trim());
        if (
          // !isSeePresent && // ← skip final-screen if SEE exists
          (hasNullValueForFinalSelection ||
            selectedCode.rawDescription === null) &&
          newState.pendingInstructions?.length === 0
        ) {
          newState = {
            ...newState,
            lastAddedCode: selectedCode,
            showFinalScreen: true,
            showError: true,
            selectedParent: selectedCode,
          };
          setTimeout(() => {
            reduxDispatch(updateLocalState(newState));
          }, 0);
          pushHistoryState(newState);
          return;
        }

        let codesToAdd = [];

        if (selectedCode.isFromTabular) {
          codesToAdd.push({
            rawName: selectedCode.rawName,
            rawDescription:
              selectedCode.description ||
              selectedCode.name ||
              "Tabular Code Description",
            description:
              selectedCode.description ||
              selectedCode.name ||
              "Tabular Code Description",
            isFromTabular: true,
            acs: selectedCode.acs,
            code_first_instruction: selectedCode.code_first_instruction,
            use_additional_instruction: selectedCode.use_additional_instruction,
            codealsowhenperf_instruction:
              selectedCode.codealsowhenperf_instruction,
          });
        }

        if (selectedCode.isUnspecified && selectedParent) {
          codesToAdd = [
            {
              rawName: selectedCode.rawName,
              rawDescription: selectedCode.description,
              isUnspecified: true,
              isFromTabular: selectedCode.isFromTabular || false,
              acs: selectedCode.acs,
              code_first_instruction: selectedCode.code_first_instruction,
              use_additional_instruction:
                selectedCode.use_additional_instruction,
              codealsowhenperf_instruction:
                selectedCode.codealsowhenperf_instruction,
                index_to_tabular_instruction:selectedCode.index_to_tabular_instruction,
            },
          ];
        }

        if (
          selectedParent &&
          (selectedCode.children?.length ||
            selectedCode.subcodes?.length ||
            0) === 0 &&
          !selectedCode.isUnspecified
        ) {
          codesToAdd.push({
            rawName: selectedCode.rawName,
            rawDescription: selectedCode.description,
            isFromTabular: selectedCode.isFromTabular || false,
            acs: selectedCode.acs,
            code_first_instruction: selectedCode.code_first_instruction,
            use_additional_instruction: selectedCode.use_additional_instruction,
            codealsowhenperf_instruction:
              selectedCode.codealsowhenperf_instruction,
              index_to_tabular_instruction:selectedCode.index_to_tabular_instruction,
          });
        }

        if (
          !selectedParent &&
          (selectedCode.children?.length ||
            selectedCode.subcodes?.length ||
            0) === 0
        ) {
          codesToAdd.push({
            rawName: selectedCode.rawName,
            rawDescription: selectedCode.description,
            isUnspecified: selectedCode.isUnspecified || false,
            isFromTabular: selectedCode.isFromTabular || false,
            acs: selectedCode.acs,
            code_first_instruction: selectedCode.code_first_instruction,
            use_additional_instruction: selectedCode.use_additional_instruction,
            codealsowhenperf_instruction:
              selectedCode.codealsowhenperf_instruction,
              index_to_tabular_instruction:selectedCode.index_to_tabular_instruction,
          });
        }
        const seen = new Set();
        codesToAdd = codesToAdd.filter((code) => {
          const key = `${code.rawName}-${code.rawDescription}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });

        let invalidCodes = [];
        codesToAdd = codesToAdd.filter((code) => {
          const hasNullValue =
            !code.rawName?.trim() ||
            code.rawDescription === null ||
            !code.rawDescription?.trim();
          if (hasNullValue) invalidCodes.push(code);
          return !hasNullValue;
        });

        if (invalidCodes.length > 0) {
          pushHistoryState({
            ...newState,
            showFinalScreen: false,
          });
          newState = {
            ...newState,
            lastAddedCode: invalidCodes[0],
            showFinalScreen: true,
            showError: true,
            selectedParent: selectedCode,
          };
          reduxDispatch(updateLocalState(newState));
          pushHistoryState(newState);
          setTimeout(() => {
            reduxDispatch(
              updateLocalState({ showError: false, lastAddedCode: null })
            );
          }, 3000);
        } else if (codesToAdd.length > 0) {
          codesToAdd.forEach((code) => {
            if (
              !isCodeInHistory(
                code.rawName,
                code.rawDescription,
                codePathHistory
              )
            ) {
              let historyNode;
              if (currentPath.length === 0) {
                historyNode = buildHistoryNode(code);
              } else {
                let currentNode = buildHistoryNode(code);
                for (let i = currentPath.length - 1; i >= 0; i--) {
                  const parentNode = {
                    ...currentPath[i],
                    children: [currentNode],
                  };
                  currentNode = parentNode;
                }
                historyNode = currentNode;
              }
              try {
                if (
                  selectedCode._type !== "parent" &&
                  selectedCode.children.length !== 0
                ) {
                  reduxDispatch(updateCodePathHistory(historyNode));
                }
              } catch (error) {
                console.error(
                  "Error in reduxDispatch(updateCodePathHistory):",
                  error
                );
              }
            }
          });

          // Filter out "No or already coded" from final codes
          const codesToAddFiltered = codesToAdd.filter(
            (code) =>
              code.rawName !== "No or already coded" &&
              code.rawDescription !== "No or already coded"
          );

          if (
            (!newState?.pendingInstructions ||
              newState.pendingInstructions.length === 0) &&
            newState.specialCodesToProcess.length === 0
          ) {
            console.log(localSelectedCodes[selectedCategory], "eojsidhzskbf");

            const finalCategory =
              selectedCategory === "CPT Tabular" || selectedCategory==="PCS Codes"
                ? "Procedure Codes"
                : selectedCategory;

            // No instructions left - finalize
            newState = {
              ...newState,
              showFinalScreen: true,

              selectedCodes: {
                ...localSelectedCodes,

                [finalCategory]: [
                  ...(localSelectedCodes[finalCategory] || []),
                  ...codesToAddFiltered,
                ],
              },

              lastAddedCode: codesToAddFiltered.length
                ? codesToAddFiltered[codesToAddFiltered.length - 1]
                : null,

              currentWarningMessage: null,
              selectedParent: selectedCode,
            };

            reduxDispatch(clearPendingInstructions());
            reduxDispatch(clearWarningMessage());
            reduxDispatch(updateLocalState(newState));
            pushHistoryState(newState);
            return;
          } else if (newState.pendingInstructions.length > 0) {
            // Instructions still pending - process next one
            const [nextInstruction, ...remaining] =
              newState.pendingInstructions;
            let displayWarning = null;
            if (nextInstruction._instructionType === "codealsowhenperf") {
              displayWarning = `Code also when perfomed: ${nextInstruction.Warning_message}`;
            } else {
              displayWarning = nextInstruction.Warning_message;
            }
            newState = {
              ...newState,
              showFinalScreen: false,
              pendingInstructions: remaining,
              selectedCodes: {
                ...localSelectedCodes,
                [selectedCategory]: [
                  ...localSelectedCodes[selectedCategory],
                  ...codesToAddFiltered,
                ],
              },
              lastAddedCode: codesToAddFiltered.length
                ? codesToAddFiltered[codesToAddFiltered.length - 1]
                : null,
              currentWarningMessage: displayWarning,
              currentInstructionIndex: 0,
            };

            reduxDispatch(setPendingInstructions(remaining));
            if (nextInstruction.Warning_message) {
              reduxDispatch(
                setCurrentWarningMessage(nextInstruction.Warning_message)
              );
            } else {
              reduxDispatch(clearWarningMessage());
            }

            (async () => {
              const extensions = await filterTabularCodes(
                nextInstruction.Next_Parent_code,
                selectedCategory
              );

              if (extensions.length > 0) {
                // pause for user selection
                const noOption = {
                  rawName: "No or already coded",
                  rawDescription: "No or already coded",
                  name: "No or already coded",
                  description: "No or already coded",
                  children: [],
                  isFromTabular: false,
                  _isCustomOption: true,
                };
                // Show children, wait for user selection
                newState.currentCodeSearchData = extensions;
                newState.filteredCodes = [noOption, ...extensions];
                newState.selectedIndex = null;
                newState.initialSearchDone = true;
                newState.showFinalScreen = false;
                newState.originalCode = normalizeCodeObject(selectedCode);
                reduxDispatch(updateLocalState(newState));
                pushHistoryState(newState);
              } else {
                // no children, treat this instruction as resolved
                const codeToAdd = {
                  rawName:
                    nextInstruction._parent?.rawName ||
                    nextInstruction.Next_Parent_code,
                  rawDescription: nextInstruction.Next_Parent_code,
                  id: nextInstruction._parent?.id || null,
                  isFromTabular:
                    nextInstruction._parent?.isFromTabular || false,
                };
                const updatedState = {
                  ...newState,
                  selectedCodes: {
                    ...localSelectedCodes,
                    [selectedCategory]: [
                      ...localSelectedCodes[selectedCategory],
                      codeToAdd,
                    ],
                  },
                  lastAddedCode: codeToAdd,
                  currentWarningMessage: null,
                };

                reduxDispatch(updateLocalState(updatedState));
                pushHistoryState(updatedState);
              }
            })();
          } else if (
            newState.specialCodesToProcess.length > 0 &&
            localState.pendingInstructions.length === 0
          ) {
            // No instructions but special codes pending - process them
            processSpecialCodesAfterInstructions();
            return;
          }
        } else {
          newState = {
            ...newState,
            showFinalScreen: true,
            // currentPath: [],
            // currentWarningMessage: null,
            selectedParent: selectedCode,
            lastAddedCode: selectedCode,
          };
          reduxDispatch(updateLocalState(newState));
          pushHistoryState(newState);
        }
      } else {
        reduxDispatch(updateLocalState(newState));
        pushHistoryState(newState);
      }
    }, [
      localState,
      processHyphenCode,
      filterTabularCodes,
      getTabularExtensions,
      codePathHistory,
      reduxDispatch,
      buildHistoryNode,
      parseSeeTerm,
      resolveSeePath,
      normalizeCodeObject,
      searchTerm,
      selectedIndex,
      filteredCodes,
      currentCodeSearchData,
      showFinalScreen,
      selectedFinalCode,
      selectedCategory,
      initialSearchDone,
      localSelectedCodes,
      selectedParent,
      topLevelParent,
      lastAddedCode,
      originalCode,
      currentPath,
      isHyphenCode,
      extractCodesFromHyphen,
      smartSplitWithBracket,
    ]);
    useEffect(() => {
      if (showFinalScreen) {
        reduxDispatch(updateFinalScreenState(true));
      } else {
        reduxDispatch(updateFinalScreenState(false));
      }
    }, [reduxDispatch, showFinalScreen]);

    const handleTabChange = useCallback(
      (key) => {
        reduxDispatch(setChildFilterTerm(""));
        reduxDispatch(setRawSearchQuery(""));
        reduxDispatch(handleResetSelectedDetails());
        reduxDispatch(resetInstructions());

        const newState = {
          searchTerm: "",
          selectedIndex: null,
          filteredCodes: [],
          currentCodeSearchData: null,
          showFinalScreen: false,
          selectedFinalCode: null,
          selectedCategory: key,
          initialSearchDone: false,
          selectedCodes: { ...localSelectedCodes },
          selectedParent: null,
          topLevelParent: null,
          lastAddedCode: null,
          originalCode: null,
          currentPath: [],
          pendingInstructions: [],
          currentInstructionIndex: 0,
          currentWarningMessage: null,
          arrivedViaBackToFinal: false,
        };
        reduxDispatch(updateLocalState(newState));
        window.history.pushState(newState, "", window.location.pathname);
      },
      [localSelectedCodes, reduxDispatch]
    );

    const handleBack = useCallback(() => {
      console.log(selectedCategory,'curated');
      console.log(localState, "handleback")
      
      if (isBackProcessing) return;
      setIsBackProcessing(true);

      // Clear child filter term
      reduxDispatch(setChildFilterTerm(""));

      // Reset injury flow if at the final or external stage
      const shouldResetInjuryFlow =
        (showFinalScreen &&
          localState.nextRangeToProcess === "externalCauseCodes") ||
        currentPath?.length <= 1;
      // Initialize injury flow
      setTimeout(() => {
        reduxDispatch(setInitialInjuryState(true));
      }, 0);

      if (shouldResetInjuryFlow) {
        console.log("🔄 BACK: Completely resetting injury flow");
        reduxDispatch(setNextRangeToProcess(null));
        reduxDispatch(setInjuryFlowTracker([]));
        reduxDispatch(setInjuryCodeFlowTracker([]));
        reduxDispatch(setInitialInjuryState(false));
        reduxDispatch(setIsInjuryWithoutActivityFlow(false));
        reduxDispatch(setFinalRangeExecuted(false));
      }

      let searchTermToAssign = "";
      let parentToRestore = null;
      let shouldClearSelectedCodeDetails = false;
      let filteredCodesToRestore = [];
      let currentCodeSearchDataToRestore = null;
      let shouldResetInstructions = false;
      let isFromTabular = false;

      const currentItem = currentPath[currentPath.length - 1];
      const isNoAlreadyCoded =
        currentItem?._isCustomOption === true ||
        currentItem?.code === "No or already coded" ||
        currentItem?.description === "No or already coded";

      const isInInstructionFlow =
        localState.pendingInstructions &&
        localState.pendingInstructions.length > 0;
      const isInSpecialCodeFlow = localState.processingSpecialCodes;

      // SIMPLIFIED SEARCH TERM ASSIGNMENT LOGIC
      const getSearchTerm = (item) => {
        if (!item) return "";

        // For tabular items, use description
        if (item.isFromTabular) {
          return item.rawDescription || item.description || item.rawName || "";
        }

        // For index items, use name/code
        return (
          item.rawName || item.name || item.code || item.rawDescription || ""
        );
      };

      // FIX 1: Don't reset instructions in instruction flow
      if (isInSpecialCodeFlow) {
        reduxDispatch(removeSecondLastFromHistory());

        if (localState.selectedParent) {
          parentToRestore = localState.selectedParent;
          isFromTabular = parentToRestore.isFromTabular || false;
          const potentialSearchTerm = getSearchTerm(parentToRestore);
          if (potentialSearchTerm !== "No or already coded") {
            searchTermToAssign = potentialSearchTerm;
          }

          // Restore children
          const children =
            parentToRestore.children || parentToRestore.subcodes || [];
          const validChildren = children
            .filter((child) => child && child.name?.trim())
            .map((child) => ({
              rawName: child.name,
              rawDescription:
                child.description ||
                (child.description === null ? null : child.name),
              name: child.name,
              description:
                child.description ||
                (child.description === null ? null : child.name),
              children: child.subcodes || child.children || [],
              isFromTabular: parentToRestore.isFromTabular || false,
              Next_parent_code: child.Next_parent_code,
              Warning_message: child.Warning_message,
              see: child.see,
              seealso: child.seealso,
              isDescriptionNull: child.description === null,
              id: child.id,
              acs: child.acs,
              code_first_instruction: child.code_first_instruction,
              use_additional_instruction: child.use_additional_instruction,
              codealsowhenperf_instruction: child.codealsowhenperf_instruction,
            }));

          filteredCodesToRestore = validChildren;
          currentCodeSearchDataToRestore = validChildren;
        }
        // Don't reset instructions in special code flow
        shouldResetInstructions = false;
      } else if (showFinalScreen || isInInstructionFlow) {
        reduxDispatch(removeSecondLastFromHistory());

        if (isInInstructionFlow) {
          // FIX 2: Preserve instruction state when going back from instruction flow
          const originalCodeForInstructions =
            localState.originalCode || selectedParent || topLevelParent;

          if (originalCodeForInstructions) {
            isFromTabular = originalCodeForInstructions.isFromTabular || false;
            const potentialSearchTerm = getSearchTerm(
              originalCodeForInstructions
            );
            if (potentialSearchTerm !== "No or already coded") {
              searchTermToAssign = potentialSearchTerm;
            }
            parentToRestore = originalCodeForInstructions;
            const children =
              originalCodeForInstructions.children ||
              originalCodeForInstructions.subcodes ||
              [];
            const validChildren = children
              .filter((child) => child && child.name?.trim())
              .map((child) => ({
                rawName: child.name,
                rawDescription:
                  child.description ||
                  (child.description === null ? null : child.name),
                name: child.name,
                description:
                  child.description ||
                  (child.description === null ? null : child.name),
                children: child.subcodes || child.children || [],
                isFromTabular:
                  originalCodeForInstructions.isFromTabular || false,
                Next_parent_code: child.Next_parent_code,
                Warning_message: child.Warning_message,
                see: child.see,
                seealso: child.seealso,
                isDescriptionNull: child.description === null,
                id: child.id,
                acs: child.acs,
                code_first_instruction: child.code_first_instruction,
                use_additional_instruction: child.use_additional_instruction,
                codealsowhenperf_instruction:
                  child.codealsowhenperf_instruction,
              }));

            filteredCodesToRestore = validChildren;
            currentCodeSearchDataToRestore = validChildren;
          }
          // Don't reset instructions - preserve the flow
          shouldResetInstructions = false;
        } else {
          // Final screen back navigation
          if (currentPath && currentPath.length > 0) {
            const targetIndex =
              currentPath.length > 1
                ? currentPath.length - 2
                : currentPath.length - 1;
            const targetPathItem = currentPath[targetIndex];
            isFromTabular = targetPathItem.isFromTabular || false;
            const potentialSearchTerm = getSearchTerm(targetPathItem);
            // ✅ Get last added code details
            const lastCode = localState.lastAddedCode;

            // ✅ Check if last added code is missing or was "No or already coded"
            const isInvalidLastCode =
              !lastCode ||
              lastCode.description === "No or already coded" ||
              lastCode.name === "No or already coded" ||
              lastCode.code === "No or already coded";

            // ✅ Decide search term based on conditions
            if (isInvalidLastCode) {
              // Reset search if last code invalid or was "No or already coded"
              searchTermToAssign = "";
              shouldClearSelectedCodeDetails = true;
              shouldResetInstructions = true;
            } else if (potentialSearchTerm !== "No or already coded") {
              // ✅ Normal case — assign search term
              searchTermToAssign = potentialSearchTerm;
            }
            if (potentialSearchTerm !== "No or already coded") {
              searchTermToAssign = potentialSearchTerm;
            }

            if (currentPath.length === 1) {
              parentToRestore = topLevelParent;
              shouldResetInstructions = true;
            } else {
              parentToRestore = targetPathItem; //null buggggggggggg nontuberculouschange
            }
          } else if (selectedParent) {
            isFromTabular = selectedParent.isFromTabular || false;
            const potentialSearchTerm = getSearchTerm(selectedParent);
            if (potentialSearchTerm !== "No or already coded") {
              searchTermToAssign = selectedParent;
            }
            parentToRestore = selectedParent;
          }

          // Re-filter codes if we have a search term
          if (searchTermToAssign && searchTermToAssign.length >= 3) {
            const refilteredCodes = filterCodes(
              searchTermToAssign,
              dataSource,
              false,
              selectedCategory
            );
            filteredCodesToRestore = refilteredCodes;
            currentCodeSearchDataToRestore = refilteredCodes;
          }
        }
      } else {
        reduxDispatch(removeSecondLastFromHistory());

        if (currentPath && currentPath.length > 0 && selectedParent !== null) {
          const targetIndex =
            currentPath.length > 1
              ? currentPath.length - 2
              : currentPath.length - 1;
          const targetPathItem = currentPath[targetIndex];
          isFromTabular = targetPathItem.isFromTabular || false;
          const potentialSearchTerm = getSearchTerm(targetPathItem);
          if (potentialSearchTerm !== "No or already coded") {
            searchTermToAssign = potentialSearchTerm;
          }
          parentToRestore = targetPathItem;

          if (targetIndex === 0 && topLevelParent) {
            parentToRestore = topLevelParent;
          } else if (slectedParntesandChilds?.parent) {
            parentToRestore = slectedParntesandChilds.parent;
          }

          if (currentPath.length === 1) {
            shouldClearSelectedCodeDetails = true;
            shouldResetInstructions = true;
          }
        } else if (selectedParent) {
          isFromTabular = selectedParent.isFromTabular || false;
          const potentialSearchTerm = getSearchTerm(selectedParent);
          if (potentialSearchTerm !== "No or already coded") {
            searchTermToAssign = potentialSearchTerm;
          }
          parentToRestore = selectedParent;

          if (slectedParntesandChilds?.parent) {
            parentToRestore = slectedParntesandChilds.parent;
            isFromTabular =
              slectedParntesandChilds.parent.isFromTabular || false;
            const potentialSearchTerm = getSearchTerm(
              slectedParntesandChilds.parent
            );
            if (potentialSearchTerm !== "No or already coded") {
              searchTermToAssign = potentialSearchTerm;
            }
          }
        } else {
          searchTermToAssign = localState.searchTerm || "";
          shouldClearSelectedCodeDetails = true;
          shouldResetInstructions = true;
          isFromTabular = false;
        }

        if (!shouldResetInstructions) {
          filteredCodesToRestore = localState.filteredCodes || [];
          currentCodeSearchDataToRestore =
            selectedParent === null ? null : localState.currentCodeSearchData;
        }
      }

      // FIX 3: Only reset instructions when explicitly needed
      if (shouldResetInstructions) {
        reduxDispatch(resetInstructions());
      }

      // Store the parent for restoration
      if (parentToRestore) {
        reduxDispatch(handleStoreParentAndChilds(parentToRestore));
        reduxDispatch(setSearchByCodeId(parentToRestore.id));
      }

      // Set the search query and reset search state
      if (searchTermToAssign) {
        console.log("check6");
        
        reduxDispatch(setRawSearchQuery(searchTermToAssign));

        if (parentToRestore?.id) {
          reduxDispatch(
            handleResetSearch({
              id: parentToRestore.id,
              rawSearchQuery: searchTermToAssign,
            })
          );
        } else {
          reduxDispatch(
            handleResetSearch({
              rawSearchQuery: searchTermToAssign,
            })
          );
        }
      } else {
        reduxDispatch(setRawSearchQuery(""));
        reduxDispatch(handleResetSelectedDetails());
        reduxDispatch(
          handleResetSearch({
            rawSearchQuery: "",
          })
        );
      }

      // FIX 4: Preserve pendingInstructions and lastAddedCode when in instruction/special code flow
      const newState = {
        ...localState,
        searchTerm: searchTermToAssign,
        showFinalScreen: false,
        pendingInstructions: shouldResetInstructions
          ? []
          : localState.pendingInstructions,
        currentInstructionIndex: shouldResetInstructions
          ? 0
          : localState.currentInstructionIndex,
        processingSpecialCodes: shouldResetInstructions
          ? false
          : localState.processingSpecialCodes,
        specialCodesToProcess: shouldResetInstructions
          ? []
          : localState.specialCodesToProcess,
        currentSpecialCode: shouldResetInstructions
          ? null
          : localState.currentSpecialCode,
        nextRangeToProcess: shouldResetInjuryFlow
          ? null
          : localState.nextRangeToProcess,
        injuryFlowTracker: shouldResetInjuryFlow
          ? []
          : localState.injuryFlowTracker,
        injuryCodeFlowTracker: shouldResetInjuryFlow
          ? []
          : localState.injuryCodeFlowTracker,
        initialInjuryState: shouldResetInjuryFlow
          ? false
          : localState.initialInjuryState,
        isInjuryWithoutActivityFlow: shouldResetInjuryFlow
          ? false
          : localState.isInjuryWithoutActivityFlow,
        finalRangeExecuted: shouldResetInjuryFlow
          ? false
          : localState.finalRangeExecuted,
        // Add other properties as needed
      };

      if (shouldClearSelectedCodeDetails) {
        reduxDispatch(clearSelectedCodeDetails());
      }

      // Update local state
      reduxDispatch(updateLocalState(newState));

      // Update browser history
      window.history.replaceState(newState, "", window.location.pathname);

      // Navigate back if we have history
      if (
        currentPath?.length > 0 ||
        selectedParent !== null ||
        showFinalScreen
      ) {
        window.history.back();
      }

      // Set tabs after navigation
      setTimeout(() => {
        console.log("isFromTabular value:", isFromTabular, selectedCategory);
        console.log("typeOfPatient:", typeOfPatient);
        // Determine which category group we're in
        const isDiagnosisCategory =
          (typeOfPatient !== "outPatient" && (selectedCategory === "Principal Diagnosis" || selectedCategory === "Additional Diagnoses"));

        console.log(isDiagnosisCategory,'isDiagnosisCategory');
        
        const isProcedureCategory =
          (typeOfPatient === "outPatient" && selectedCategory === "PCS Codes") ||
          (typeOfPatient !== "outPatient" && selectedCategory === "Procedure Codes");
       
        if (isDiagnosisCategory) {
          console.log("Setting ICD 10 CM tab");
          if (isFromTabular) {
            reduxDispatch(setActiveTab("ICD 10 CM Tabular"));
            reduxDispatch(setActiveIcon("Tabular"));
          } else {
            reduxDispatch(setActiveTab("ICD 10 CM Index"));
            reduxDispatch(setActiveIcon("ICD"));
          }
        } else if (isProcedureCategory) {
          console.log("Setting Procedure Codes tab");
          if (isFromTabular) {
            reduxDispatch(setActiveTab("PCS Tabular"));
            reduxDispatch(setActiveIcon("Tabular"));
          } else {
            reduxDispatch(setActiveTab("PCS Index"));
            reduxDispatch(setActiveIcon("ICD"));
          }
        } else {
          console.log("No matching category found:", selectedCategory);
        }
      }, 100);
      setTimeout(() => {
        setIsBackProcessing(false);
      }, 200);
    }, [
      isBackProcessing,
      currentPath,
      selectedParent,
      topLevelParent,
      slectedParntesandChilds,
      selectedCategory,
      localSelectedCodes,
      localState,
      showFinalScreen,
      initialSearchDone,
      reduxDispatch,
      normalizeCodeObject,
      dataSource,
      filterCodes,
    ]);
    // Add this function to extract cluster IDs from existing codes
    const extractExistingClusterIds = useCallback(() => {
      const existingClusterIds = new Set();

      // Check if there are existing codes in sessionStorage or from props
      const existingFinalCode = sessionStorage.getItem("finalCode");

      if (existingFinalCode) {
        try {
          const parsedData = JSON.parse(existingFinalCode);

          // Extract from principal diagnoses
          if (parsedData.principal && Array.isArray(parsedData.principal)) {
            parsedData.principal.forEach((item) => {
              if (item.code && item.code.length >= 2) {
                // Extract cluster ID (second character after COF number)
                const clusterId = item.code.charAt(1);
                if (/[A-Z]/.test(clusterId)) {
                  existingClusterIds.add(clusterId);
                }
              }
            });
          }

          // Extract from additional diagnoses
          if (parsedData.additional && Array.isArray(parsedData.additional)) {
            parsedData.additional.forEach((item) => {
              if (item.code && item.code.length >= 2) {
                const clusterId = item.code.charAt(1);
                if (/[A-Z]/.test(clusterId)) {
                  existingClusterIds.add(clusterId);
                }
              }
            });
          }

          // Extract from procedures
          if (parsedData.procedure && Array.isArray(parsedData.procedure)) {
            parsedData.procedure.forEach((item) => {
              if (item.code && item.code.length >= 2) {
                const clusterId = item.code.charAt(1);
                if (/[A-Z]/.test(clusterId)) {
                  existingClusterIds.add(clusterId);
                }
              }
            });
          }
        } catch (error) {
          console.error("Error parsing existing finalCode:", error);
        }
      }

      return Array.from(existingClusterIds).sort();
    }, []);

    const startNewCluster = useCallback(() => {
      // Extract existing cluster IDs from stored codes
      const existingClusterIds = extractExistingClusterIds();

      // Get next cluster ID considering existing ones
      const newClusterId = clusterIdGenerator.getNext(existingClusterIds);

      // Update Redux state
      reduxDispatch(setCurrentClusterId(newClusterId));
      reduxDispatch(
        addToClusterHistory({
          id: newClusterId,
          timestamp: new Date().toISOString(),
          existingIds: existingClusterIds,
        })
      );

      return newClusterId;
    }, [reduxDispatch, extractExistingClusterIds]);
    const handleFinalize = useCallback(async () => {
                const newState = {
          searchTerm: "",
          selectedIndex: null,
          filteredCodes: [],
          currentCodeSearchData: null,
          selectedFinalCode: null,
          selectedCategory: selectedCategory,
          initialSearchDone: false,
          selectedCodes: { ...localSelectedCodes },
          selectedParent: null,
          topLevelParent: null,
          lastAddedCode: null,
          originalCode: null,
          currentPath: [],
          pendingInstructions: [],
          currentInstructionIndex: 0,
          currentWarningMessage: null,
          arrivedViaBackToFinal: false,
        };
        reduxDispatch(updateLocalState(newState));
        window.history.pushState(newState, "", window.location.pathname);
      try {
        reduxDispatch(handleResetSearch());
        reduxDispatch(setActiveTab("ICD 10 CM Index"));
        reduxDispatch(setActiveIcon("ICD"));
        setIsFetchingData1(true);

        let clusterIdToUse = currentClusterId;
        if (!clusterIdToUse) {
          clusterIdToUse = startNewCluster();
        }

        const allTabsData = Object.entries(localSelectedCodes).reduce(
          (acc, [category, codes]) => {
            const codesForTab = [];
            const processedKeys = new Set();

            // First, collect all codes
            codes.forEach((selectedCode) => {
              const description = selectedCode.rawDescription || "";
              const name = selectedCode.rawName || "";
              const key = `${name}-${description}`;

              if (!processedKeys.has(key)) {
                const codesArray = description
                  .split("†")
                  .map((codePart) => codePart.trim().replace(/\*$/, ""));
                const finalCodesArray =
                  codesArray.length > 1
                    ? codesArray
                    : [description.trim().replace(/\*$/, "")];

                if (selectedCode.isFromTabular) {
                  // For tabular codes: prefix the code with cluster ID
                  const baseCode = finalCodesArray[0] || name;
                  // const prefixedCode = `${clusterIdToUse}${name}`;
                  const prefixedCode = `${name}`;

                  codesForTab.push({
                    code: prefixedCode, // This will be like "AI23.2"
                    codes: [prefixedCode],
                    rawName: selectedCode.rawName,
                    rawDescription: selectedCode.rawDescription,
                    // Add a flag to indicate if it starts with T
                    startsWithT: selectedCode.rawDescription?.startsWith("T"),
                  });
                } else {
                  // For index codes: prefix each code in the array
                  // const prefixedCodes = finalCodesArray.map(code =>
                  //   `${clusterIdToUse}${code}`
                  // );
                  const prefixedCodes = finalCodesArray.map(
                    (code) => `${code}`,
                  );

                  codesForTab.push({
                    code: `${name || ""}`,
                    codes: prefixedCodes,
                    rawName: selectedCode.rawName,
                    rawDescription: selectedCode.rawDescription,
                    // Add a flag to indicate if it starts with T
                    startsWithT: selectedCode.rawDescription?.startsWith("T"),
                  });
                }
                processedKeys.add(key);
              }
            });

            // Now sort the codes: T codes first, then others
            const sortedCodesForTab = codesForTab.sort((a, b) => {
              if (a.startsWithT && !b.startsWithT) return -1;
              if (!a.startsWithT && b.startsWithT) return 1;
              return 0;
            });

            // Remove the temporary flag before storing
            const finalCodes = sortedCodesForTab.map(
              ({ startsWithT, ...rest }) => rest,
            );

            acc[category] = finalCodes;
            return acc;
          },
          {},
        );

        if (onFinalizeCode) {
          onFinalizeCode(allTabsData, clusterIdToUse);
        }

        // Store the final codes
        sessionStorage.finalCode = JSON.stringify(allTabsData);

        reduxDispatch(clearCodePathHistory());

        // Wait for fetchData1() to complete before proceeding
        await fetchData1();

        const finalcode = sessionStorage.getItem("finalCode");

        setfilecode(allTabsData);

        handleCodeAssistAdding(finalcode);
        setShowCodeSearch(false);
      } catch (error) {
        console.error("Error in handleFinalize:", error);
      } finally {
        setIsFetchingData1(false);
      }
    }, [
      onFinalizeCode,
      setfilecode,
      setShowCodeSearch,
      localSelectedCodes,
      fetchData1,
      reduxDispatch,
      currentClusterId,
      startNewCluster,
    ]);

    useEffect(() => {
      if (selectedIndex !== null) {
        const timer = setTimeout(() => {
          handleContinue(selectedIndex);
        }, 100);

        return () => clearTimeout(timer);
      }
    }, [selectedIndex]);

    const currentCategory = selectedCategory;
    const filterChildCodes = useCallback((term, codes) => {
      if (!term || term.length < 2) {
        return codes;
      }

      const normalizedTerm = term.toLowerCase().replace(/\s+/g, " ").trim();

      // strict equality (exact match)
      const exactMatches = codes.filter((child) => {
        const name = (child.rawName || child.name || "").toLowerCase().trim();
        const description = (child.rawDescription || child.description || "")
          .toLowerCase()
          .trim();
        return name === normalizedTerm || description === normalizedTerm;
      });

      if (exactMatches.length > 0) {
        return exactMatches;
      }

      //fallback to prefix search (word starts with the term)
      return codes.filter((child) => {
        const name = (child.rawName || child.name || "").toLowerCase().trim();
        const description = (child.rawDescription || child.description || "")
          .toLowerCase()
          .trim();

        return (
          name.startsWith(normalizedTerm) ||
          description.startsWith(normalizedTerm)
        );
      });
    }, []);

    useEffect(() => {
      if (
        initialSearchDone &&
        slectedParntesandChilds &&
        slectedParntesandChilds.parentName
      ) {
        const childTermName =
          slectedParntesandChilds.rawName || slectedParntesandChilds.name || "";
        reduxDispatch(setChildFilterTerm(childTermName));
      } else {
        reduxDispatch(setChildFilterTerm(""));
      }
    }, [slectedParntesandChilds, initialSearchDone, reduxDispatch]);

    const codeList = useMemo(() => {
      const selectedCode = localState.selectedCode;

      const hasInstructions =
        (Array.isArray(slectedParntesandChilds?.code_first_instruction) &&
          slectedParntesandChilds.code_first_instruction.length > 0) ||
        (Array.isArray(slectedParntesandChilds?.use_additional_instruction) &&
          slectedParntesandChilds.use_additional_instruction.length > 0);

      const hasNextParentOptions = filteredCodes?.some(
        (code) => code.isNextParentOption
      );

      const shouldFilterChildren = initialSearchDone && childFilterTerm;

      // Filter out codes that are in injuryCodeFlowTracker
      const filteredCodesWithoutInjuryTracker =
        filteredCodes?.filter((code) => code) || [];

      const displayedCodes = shouldFilterChildren
        ? filterChildCodes(childFilterTerm, filteredCodesWithoutInjuryTracker)
        : filteredCodesWithoutInjuryTracker;
      // console.log(displayedCodes, "displayedCodes");

      const getCodeId = (code) => {
        if (code.id) return code.id;
        if (code.isParentOption && code.children?.[0]?.id)
          return code.children[0].id;
        if (code.parent?.id) return code.parent.id;
        if (code.isUnspecified && code.originalCode?.id)
          return code.originalCode.id;
        return null;
      };

      return (
        <>
          {localState.currentWarningMessage && (
            <p className="text-center py-1 px-2 mb-2 small code-search-warning">
              ℹ️ {localState?.currentWarningMessage} ?
            </p>
          )}

          {displayedCodes.length > 0 ? (
            <ListGroup className="codesearch-codeassist">
              {displayedCodes.map((code, index) => {
                const displayLabel = code.isFromTabular
                  ? code.rawDescription || code.rawName
                  : code.rawName || code.rawDescription;
                const typeLabel = !initialSearchDone
                  ? // ? code.parentName || "Lead Term"
                    code.see
                    ? `see ${code.see}`
                    : code.parentName || "Lead Term"
                  : null;
                const codeId = getCodeId(code);
                const originalIndex =
                  filteredCodesWithoutInjuryTracker.findIndex(
                    (c) => c.id === code.id
                  );

                return (
                  <ListGroup.Item
                    key={codeId || index}
                    className="d-flex align-items-center justify-content-between"
                  >
                    <Form.Check
                      type="radio"
                      name="codeOptions"
                      id={`code-${index}`}
                      className="list-item-noActive"
                      label={
                        code.isUnspecified || code.isSeeAlsoUnspecified
                          ? `Unspecified (${displayLabel})`
                          : code.isNextParentOption
                          ? code.rawName
                          : displayLabel
                      }
                      checked={selectedIndex === originalIndex}
                      onChange={() => {
                        reduxDispatch(setSelectedIndex(originalIndex));
                        reduxDispatch(
                          handleSelectedCodeDetails({
                            category: selectedCategory,
                            code_id:
                              code.isSeeAlsoOption && code.referenceId
                                ? code.referenceId
                                : codeId,
                            value: code.isFromTabular
                              ? code.rawDescription
                              : code.rawName,
                            isFromTabular: !!code.isFromTabular,
                            acs: code.acs,
                            code_first_instruction:
                              code.code_first_instruction || [],
                            use_additional_instruction:
                              code.use_additional_instruction || [],
                            codealsowhenperf_instruction:
                              code.codealsowhenperf_instruction || [],
                            children: code.children || [],
                            // see:code.see,
                          })
                        );
                      }}
                    />
                    {typeLabel && (
                      <small
                        className="badge rounded-pill bg-light text-muted border"
                        style={{ fontSize: "0.75rem" }}
                      >
                        {typeLabel}
                      </small>
                    )}
                  </ListGroup.Item>
                );
              })}
            </ListGroup>
          ) : shouldFilterChildren && childFilterTerm ? (
            <p className="text-muted text-center mt-4">
              No matching child codes found
            </p>
          ) : null}
        </>
      );
    }, [
      filteredCodes,
      selectedIndex,
      initialSearchDone,
      selectedCategory,
      childFilterTerm,
      filterChildCodes,
      reduxDispatch,
      localState.currentWarningMessage,
      injuryCodeFlowTracker, // Add this dependency
    ]);

    const renderSearchAndList = () => (
      <>
        {!initialSearchDone && (
          <div className="search-bar-code mt-3">
            <>
              <FaSearch className="codebook-search-icon" />
              <Form.Control
                type="text"
                className="search-input"
                placeholder="Search by description..."
                value={searchTerm}
                onChange={(e) => {
                  const newSearchTerm = e.target.value;
                  const shouldClearResults = newSearchTerm.length < 3;
                  reduxDispatch(
                    updateLocalState({
                      searchTerm: newSearchTerm,
                      topLevelParent: null,
                      currentPath: [],
                      initialSearchDone: false,
                      filteredCodes: shouldClearResults ? [] : filteredCodes,
                    })
                  );
                  if (newSearchTerm.length === 0) {
                    reduxDispatch(setRawSearchQuery(""));
                    reduxDispatch(setSearchByCodeId(null));
                  }
                  window.history.replaceState(
                    {
                      ...localState,
                      searchTerm: newSearchTerm,
                      topLevelParent: null,
                      currentPath: [],
                      filteredCodes: shouldClearResults ? [] : filteredCodes,
                    },
                    "",
                    window.location.pathname
                  );
                }}
              />
            </>
          </div>
        )}
        {selectedParent && initialSearchDone && topLevelParent && (
          <div className="selected-parent-codeassist">
            <p>
              {slectedParntesandChilds?._type === "child" ? (
                <>
                  <span
                    style={{
                      fontWeight: "bold",
                      fontSize: "15px",
                      color: "black",
                    }}
                  >
                    Selected Parent:
                  </span>{" "}
                  {topLevelParent?.parentName}
                </>
              ) : (
                <>
                  <span
                    style={{
                      fontWeight: "bold",
                      fontSize: "15px",
                      color: "black",
                    }}
                  >
                    Selected Parent:
                  </span>
                  {selectedParent.isFromTabular
                    ? selectedParent.rawDescription || selectedParent.rawName
                    : selectedParent.rawName || selectedParent.rawDescription}
                </>
              )}
            </p>
          </div>
        )}

        {initialSearchDone && (
          <div className="search-bar-code mt-3">
            <FaSearch className="codebook-search-icon" />
            <Form.Control
              type="text"
              className="search-input"
              placeholder="Search..."
              value={childFilterTerm}
              onChange={(e) =>
                reduxDispatch(setChildFilterTerm(e.target.value))
              }
            />
          </div>
        )}

        {codeList}
        {searchTerm.length >= 3 && filteredCodes.length === 0 && (
          <p className="text-muted text-center mt-4">No results found</p>
        )}
      </>
    );

    const handleCodeSearchRef = (acsCode) => {
      reduxDispatch(updateAcsCode(null));
      setTimeout(() => {
        reduxDispatch(updateAcsCode(acsCode));
      }, 0);
      reduxDispatch(setActiveTab("Australian Coding Standard"));
      reduxDispatch(setActiveIcon("Coding"));
    };
    const findCodeDescriptionInTabular = useCallback(
      (codeName, category) => {
        const cleanedCode = codeName?.replace(/[()]/g, "");
        if (category === "Procedure Codes" || category === "PCS Codes") {
          return findCodeInTabularData(cleanedCode, pcsTabularData);
        } else {
          return findCodeInTabularData(cleanedCode, typeOfPatient==="outPatient"?pcsIndexData:icd10amTabularData);
        }
      },
      [icd10amTabularData,pcsTabularData,pcsIndexData]
    );

    const findCodeInTabularData = (codeName, tabularData) => {
      if (!tabularData || !Array.isArray(tabularData)) return null;

      const normalizedCode = codeName?.trim().toUpperCase();
      if (!normalizedCode) return null;

      let exactMatch = null;
      let rangeMatch = null;

      const searchRecursive = (items) => {
        for (const item of items) {
          const itemCode = item.name?.trim().toUpperCase();
          const itemDesc = item.description || item.rawDescription;

          // 1. Check for EXACT match first
          if (itemCode === normalizedCode) {
            exactMatch = itemDesc;
            return true; // Stop searching when exact match found
          }

          // 2. Check for range match (but don't stop searching yet)
          if (itemCode && itemCode.includes("-")) {
            const [rangeStart, rangeEnd] = itemCode
              .split("-")
              .map((part) => part.trim().toUpperCase());

            if (
              rangeStart &&
              rangeEnd &&
              normalizedCode >= rangeStart &&
              normalizedCode <= rangeEnd
            ) {
              rangeMatch = itemDesc; // Store range match but keep looking for exact
            }
          }

          // Continue searching in nested structures
          if (item.children) {
            if (searchRecursive(item.children)) return true;
          }
          if (item.subcodes) {
            if (searchRecursive(item.subcodes)) return true;
          }
          if (item.codes) {
            if (searchRecursive(item.codes)) return true;
          }
        }
        return false;
      };

      searchRecursive(tabularData);

      // Return exact match first, then range match, then null
      return exactMatch || rangeMatch || null;
    };

    const renderFinalScreen = () => {
      let hasDisplayError = showError;

      // Function to prevent duplicates across all categories
      const getUniqueCodes = (codes, category) => {
        const seen = new Set();
        const uniqueCodes = [];

        codes.forEach((code, index) => {
          // Create a unique key that works for both ICD-10 AM and ACHI
          const key = code.isFromTabular
            ? `${category}-${code.rawName}-${code.rawDescription}`
            : `${category}-${code.rawDescription}-${code.rawName}`;

          if (!seen.has(key)) {
            seen.add(key);
            uniqueCodes.push({ ...code, originalIndex: index });
          }
        });

        return uniqueCodes;
      };
      const buttonLabelVariant2 =
        typeOfPatient === "outPatient"
          ? selectedCategory === "CPT Tabular"
            ? "Add CPT"
            : selectedCategory === "PCS Codes"
            ? "Add PCS"
            : "Add Procedure"
          : selectedCategory === "Additional Diagnoses"
          ? "Add Procedure"
          : "Add Additional Diagnoses";

      return (
        <div>
          {hasDisplayError && (
            <p
              className="alert alert-danger text-center py-1 px-2 mb-2 small"
              role="alert"
            >
              ⚠️ selected value has a null code!
            </p>
          )}
          <div className="mt-2 finalcodesearchbuttons codesearch-codeassist">
            {Object.entries(localSelectedCodes)
              .filter(([_, codes]) => codes && codes.length > 0)
              .map(([category, codes]) => {
                // Get unique codes for this category
                const uniqueCodes = getUniqueCodes(codes, category);

                return (
                  <div
                    key={category}
                    className="mb-3 codesearch-tab-name-change"
                  >
                    <p className="selectedcategorycodesearch">{category}</p>
                    <ListGroup>
                      {uniqueCodes.map((code, displayIndex) => {
                        const hasInvalidFields =
                          !code.rawName?.trim() || !code.rawDescription?.trim();
                        if (hasInvalidFields) {
                          if (!hasDisplayError) {
                            hasDisplayError = true;
                            reduxDispatch(
                              updateLocalState({ showError: true })
                            );
                            setTimeout(() => {
                              reduxDispatch(
                                updateLocalState({ showError: false })
                              );
                            }, 3000);
                          }
                          return null;
                        }

                        // ALWAYS get the description from tabular data for consistency
                        const tabularDescription = findCodeDescriptionInTabular(
                          code.rawDescription,
                          category
                        );
                        // console.log(code, "asdasdasdpppp");

                        // Use tabular description as primary, fallback to existing description
                        const finalDescription =
                          tabularDescription || code.rawName;
                        const codeDisplay = code.rawDescription;

                        const hasInvalidDisplay =
                          !finalDescription?.trim() || !codeDisplay?.trim();
                        if (hasInvalidDisplay) {
                          if (!hasDisplayError) {
                            hasDisplayError = true;
                            reduxDispatch(
                              updateLocalState({ showError: true })
                            );
                            setTimeout(() => {
                              reduxDispatch(
                                updateLocalState({ showError: false })
                              );
                            }, 3000);
                          }
                          return null;
                        }

                        return (
                          <ListGroup.Item
                            key={`${category}-${displayIndex}`}
                            className="code-description-item d-flex justify-content-between align-items-start"
                          >
                            <div className="d-flex flex-column w-100">
                              <div className="d-flex justify-content-between align-items-center">
                                {!code.isFromTabular ? (
                                  <>
                                    <span className="description-span fw-bold">
                                      {codeDisplay}
                                    </span>
                                    <span className="code-span text-muted">
                                      {finalDescription}
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    <span className="description-span fw-bold">
                                      {code.rawName}
                                    </span>
                                    <span className="code-span text-muted">
                                      {codeDisplay}
                                    </span>
                                  </>
                                )}

                                {/* {code?.acs && (
                                  <button
                                    className="acs-btn ms-2"
                                    onClick={() =>
                                      handleCodeSearchRef(code.acs)
                                    }
                                  >
                                    ACS
                                  </button>
                                )} */}
                              </div>
                            </div>
                          </ListGroup.Item>
                        );
                      })}
                    </ListGroup>
                  </div>
                );
              })}
          </div>
          <div className="d-flex justify-content-between mt-4 finalcodesearchbuttons">
            <Button
              className="codeserach-back"
              onClick={handleBack}
              disabled={isBackProcessing}
            >
              {showFinalScreen && arrivedViaBackToFinal ? "" : "Back"}
            </Button>
            <Button
              className="codesearch-continue1 d-flex align-items-center justify-content-center"
              onClick={handleFinalize}
            >
              {isFetchingData1 || enableFinalize ? "Adding.." : "Continue"}{" "}
              <span className="continue-check">
                <FaCheck />
              </span>
            </Button>
            <Button
              className="codesearch-continue1 d-flex align-items-center justify-content-center"
              onClick={() => {
                reduxDispatch(
                  updateLocalState({
                    ...localState,
                    pendingInstructions: [],
                    currentWarningMessage: null,
                    nextRangeToProcess: null,
                  })
                );

                reduxDispatch(setInitialInjuryState(true));
                sessionStorage.setItem("newcycle", "false");
                reduxDispatch(setInjuryFlowTracker([]));
             
                

                if (typeOfPatient === "outPatient") {
                  if (selectedCategory === "CPT Tabular") {
                    handleTabChange("CPT Tabular"); // Add CPT clicked → go to PCS
                  } else if (selectedCategory === "PCS Codes") {
                    handleTabChange("PCS Codes"); // Add PCS → go back to CPT
                  } else {
                    handleTabChange("CPT Tabular"); // default for outpatient
                  }
                } else {
                  // Not outpatient
                  if (selectedCategory === "Additional Diagnoses") {
                    handleTabChange("Procedure Codes"); // Add Procedure
                setTimeout(()=>{
                      reduxDispatch(setActiveTab("PCS Index"));
                    reduxDispatch(setActiveIcon("ICD"));
                },0)
                  } else {
                    handleTabChange("Additional Diagnoses"); // Add Additional Diagnoses
                    reduxDispatch(setActiveTab("ICD 10 CM Index"))
                    reduxDispatch(setActiveIcon("ICD"))
                  }
                }
                if(selectedCategory==="CPT Tabular"){
                   setTimeout(() => {
                          reduxDispatch(setActiveTab("CPT Tabular"));
                          reduxDispatch(setActiveIcon("Tabular"));
                        }, 500);
                  
                }
              }}
            >
              {buttonLabelVariant2}
            </Button>

            <Button
              className="codesearch-continue1 d-flex align-items-center justify-content-center"
              onClick={() => {
                
                if (typeOfPatient === "outPatient") {
                  if (selectedCategory === "CPT Tabular") {
                    handleTabChange("PCS Codes");
                     setTimeout(() => {
                      reduxDispatch(setActiveTab("PCS Index"));
                      reduxDispatch(setActiveIcon("ICD"));
                    }, 0);
                  } else {
                    handleTabChange("CPT Tabular");
                     setTimeout(() => {
                      reduxDispatch(setActiveTab("CPT Tabular"));
                      reduxDispatch(setActiveIcon("Tabular"));
                    }, 0);
                  }
                } else {
                  if(selectedCategory==="Additional Diagnoses"){
                    handleTabChange("Additional Diagnoses");
                    reduxDispatch(setActiveTab("ICD 10 CM Index"))
                  }else{
                  handleTabChange("Procedure Codes");
                  reduxDispatch(setActiveTab("PCS Index"))
                  }
                }
              }}
            >
              {typeOfPatient === "outPatient"
                ? selectedCategory === "CPT Tabular"
                  ? "Add PCS"
                  : "Add CPT"
                : selectedCategory==="Additional Diagnoses"?"Add Additional Diagnoses":"Add Procedure"}
            </Button>
            <Button
              className="codeserach-back"
              onClick={() => {
                setShowCodeSearch(false);
                const newState = {
                  ...localState,
                  pendingInstructions: [],
                  searchTerm: "",
                  showFinalScreen: false,
                  selectedFinalCode: null,
                  currentWarningMessage: null,
                  showError: false,
                  pendingInstructions: [], // NEW: Clear instructions
                  currentInstructionIndex: 0,
                };
                reduxDispatch(updateLocalState(newState));
                reduxDispatch(setRawSearchQuery(""));
                reduxDispatch(setSearchByCodeId(null));
                reduxDispatch(handleResetSelectedDetails());
                reduxDispatch(resetInstructions());
                setShowCodeSearch(false);
              }}
            >
              Cancel
            </Button>
            {/* <Button
              className="codeserach-back"
              onClick={handleBack}
              disabled={isBackProcessing}
            >
              {showFinalScreen && arrivedViaBackToFinal ? "" : "Back"}
            </Button> */}
          </div>
        </div>
      );
    };
    // console.log(selectedCategory, "skwjedkjw");
    const isOutPatient = typeOfPatient === "outPatient";

    const buttonLabel = isOutPatient
      ? selectedCategory === "CPT Tabular"
        ? "Add PCS"
        : selectedCategory === "PCS Codes"
        ? "Add CPT"
        : "Add Procedure"
      : selectedCategory === "Additional Diagnoses"
      ? "Add Procedure"
      : "Add Additional Diagnoses";

    return (
      <Container className="w-100 max-w-md mx-auto codeserach-codeassit">
        {showFinalScreen ? (
          <>
            {injuryFlowTracker.length > 0 && !noInjuryFlowState && (
              <div className="mb-3 mt-2 reprocess-injury-flow">
                <p className="mb-2 ">
                  Do you want to continue with injury coding?
                </p>
                <div className="d-flex gap-3">
                  <Form.Check
                    type="radio"
                    name="injuryContinue"
                    id="injury-yes"
                    label="Yes"
                    checked={localState.injuryContinueChoice === "yes"}
                    onChange={async () => {
                      sessionStorage.setItem("newcycle", "true");
                      reduxDispatch(setInitialInjuryState(false));
                      // Determine the final range based on injuryFlowTracker
                      let finalRange = "";
                      let warningMessage = "";

                      if (injuryFlowTracker.includes("withInjuries")) {
                        if (injuryFlowTracker.includes("withBurns")) {
                          finalRange = "T20-T31";
                          warningMessage = "Do you want to code more burns";
                        } else if (
                          injuryFlowTracker.includes("withFrostBites")
                        ) {
                          finalRange = "T33-T35";
                          warningMessage = "Do you want to code more injuries";
                        } else if (
                          injuryFlowTracker.includes("withPoisoning")
                        ) {
                          finalRange = "T33-T35";
                          warningMessage =
                            "Do you want to code any more poison codes";
                        } else {
                          finalRange =
                            "S00-S09,S10-S29,S20-S29,S30-S39,S40-S49,S50-S59,S60-S69,S70-S79,S80-S89,S90-S99,T00-T07,T08-T14,T20-T31,T33-T35,T66-T78,T79-T79,T80-T88,T89-T89,T90-T98";
                          warningMessage = "Do you want to code more injuries";
                        }
                      } else if (injuryFlowTracker.includes("withBurns")) {
                        finalRange = "T20-T31";
                        warningMessage = "Do you want to code more burns";
                      } else if (injuryFlowTracker.includes("withFrostBites")) {
                        finalRange = "T33-T35";
                        warningMessage = "Do you want to code more injuries";
                      } else if (injuryFlowTracker.includes("withPoisoning")) {
                        finalRange = "T33-T35";
                        warningMessage =
                          "Do you want to code any more poison codes";
                      } else if (
                        injuryFlowTracker.includes("witheffectsOfExternalCause")
                      ) {
                        finalRange = "T66-T78";
                        warningMessage =
                          "Do you want to code any effects of external cause";
                      } else if (
                        injuryFlowTracker.includes(
                          "withEarlyComplicationsOfTrauma"
                        )
                      ) {
                        finalRange = "T79-T79";
                        warningMessage =
                          "Do you want to code any early complications of trauma";
                      } else if (
                        injuryFlowTracker.includes(
                          "withComplicationsOfSurgicalAndMedicalCare"
                        )
                      ) {
                        finalRange = "T80-T88";
                        warningMessage =
                          "Do you want to code any complications from surgery and medical care";
                      } else if (
                        injuryFlowTracker.includes(
                          "withOtherComplicationsOfTrauma"
                        )
                      ) {
                        finalRange = "T89-T89";
                        warningMessage =
                          "Do you want to code any other complications of trauma";
                      } else if (
                        injuryFlowTracker.includes("withSequelaeOfInjuries")
                      ) {
                        finalRange = "T90-T98";
                        warningMessage =
                          "Do you want to code any more sequelae of injuries";
                      }
                      // Add more conditions as needed

                      if (!finalRange) {
                        return;
                      }

                      // Use the extracted function
                      const extensions =
                        await filterTabularCodesForMultipleRanges(
                          finalRange,
                          selectedCategory
                        );

                      if (extensions.length > 0) {
                        // Create Yes/No options for the final range
                        const yesOption = {
                          rawName: "Yes",
                          rawDescription: "Yes",
                          name: "Yes",
                          description: "Yes",
                          children: [],
                          isFromTabular: false,
                          _isCustomOption: true,
                          _isYesOption: true,
                          _rangeToExecute: finalRange,
                          _warningMessage: warningMessage,
                          _isFinalRange: true,
                        };

                        const noOption = {
                          rawName: "No or already coded",
                          rawDescription: "No or already coded",
                          name: "No or already coded",
                          description: "No or already coded",
                          children: [],
                          isFromTabular: false,
                          _isCustomOption: true,
                        };

                        // Reset to initial screen with the range options
                        const newState = {
                          ...localState,
                          injuryContinueChoice: "",
                          showFinalScreen: false,
                          initialSearchDone: true,
                          searchTerm: "",
                          selectedIndex: null,
                          currentCodeSearchData: [yesOption, noOption],
                          filteredCodes: extensions,
                          currentPath: [],
                          currentWarningMessage: warningMessage,
                          nextRangeToProcess: "externalCauseCodes",
                        };
                        // newState.nextRangeToProcess = "externalCauseCodes";
                        reduxDispatch(setInitialInjuryState(true));
                        reduxDispatch(updateLocalState(newState));
                        pushHistoryState(newState);
                      } else {
                        console.log(
                          "No extensions found for final range:",
                          finalRange
                        );
                        // Handle case where no extensions are found
                      }
                    }}
                  />
                  <Form.Check
                    type="radio"
                    name="injuryContinue"
                    id="injury-no"
                    label="No"
                    checked={localState.injuryContinueChoice === "no"}
                    onChange={() => {
                      setNoInjuryFlowState(true);
                      reduxDispatch(
                        updateLocalState({
                          ...localState,
                          injuryContinueChoice: "no",
                        })
                      );
                    }}
                  />
                </div>
              </div>
            )}
            {renderFinalScreen()}
          </>
        ) : (
          <>
            <Tabs
              activeKey={selectedCategory}
              id="justify-tab-example"
              className="mb-3"
              justify
              style={{ width: "fit-content" }}
              onSelect={handleTabChange}
            >
              <Tab
                eventKey={selectedCategory}
                title={selectedCategory}
                disabled
              >
                {renderSearchAndList()}
              </Tab>
            </Tabs>
            <div className="d-flex justify-content-between mt-4">
              {((selectedCategory === typeOfPatient) === "outPatient"
                ? "CPT Tabular"
                : "Additional Diagnoses" ||
                  selectedCategory === "Procedure Codes") &&
                !initialSearchDone && (
                  <Button
                    className="codesearch-continue1 d-flex align-items-center justify-content-center"
                    onClick={() => {
                      sessionStorage.setItem("newcycle", "false");
                      reduxDispatch(setInjuryFlowTracker([]));
                      reduxDispatch(
                        updateLocalState({
                          ...localState,
                          currentWarningMessage: null,
                          pendingInstructions: [],
                          nextRangeToProcess: null,
                        })
                      );
                      reduxDispatch(setInitialInjuryState(true));

                      if (selectedCategory !== "Procedure Codes") {
                        setTimeout(() => {
                          reduxDispatch(setActiveTab("PCS Index"));
                          reduxDispatch(setActiveIcon("ICD"));
                        }, 0);
                      } else {
                        setTimeout(() => {
                          reduxDispatch(setActiveTab("ICD 10 CM Index"));
                          reduxDispatch(setActiveIcon("ICD"));
                        }, 0);
                      }
                      if(selectedCategory==="PCS Codes"){
                          setTimeout(() => {
                          reduxDispatch(setActiveTab("CPT Tabular"));
                          reduxDispatch(setActiveIcon("Tabular"));
                        }, 500);
                      }

                      // 👉 FIXED LOGIC
                      if (typeOfPatient === "outPatient") {
                        if (selectedCategory === "CPT Tabular") {
                          handleTabChange("PCS Codes"); // user clicked Add PCS
                        } else if (selectedCategory === "PCS Codes") {
                          handleTabChange("CPT Tabular"); // user clicked Add CPT
                        }
                      } else {
                        if (selectedCategory === "Additional Diagnoses") {
                          handleTabChange("Procedure Codes"); // clicking Add Procedure
                        } else if (selectedCategory === "Procedure Codes") {
                          handleTabChange("Additional Diagnoses"); // clicking Add Additional Diagnoses
                        }
                      }
                    }}
                  >
                    {buttonLabel}
                  </Button>
                )}
              {initialSearchDone && (
                <Button
                  className="codeserach-back"
                  onClick={handleBack}
                  disabled={
                    isBackProcessing || (!initialSearchDone && !showFinalScreen)
                  }
                >
                  {!initialSearchDone ? "" : "Back"}
                </Button>
              )}
              {/* {!initialSearchDone &&
                Object.values(localSelectedCodes).some(
                  (codes) => codes && codes.length > 0
                ) && (
                  <Button
                    className="codeserach-back"
                    onClick={() => {
                      const newState = {
                        ...localState,
                        showFinalScreen: true,
                        currentWarningMessage: null,
                        arrivedViaBackToFinal: true,
                      };
                      reduxDispatch(updateLocalState(newState));
                      // reduxDispatch(setArrivedViaBackToFinal(true));
                      pushHistoryState(newState);
                    }}
                  >
                    Go to Final Screen
                  </Button>
                )} */}
              <Button
                className="codeserach-back"
                onClick={() => {
                  setShowCodeSearch(false);
                  const newState = {
                    ...localState,
                    pendingInstructions: [],
                    searchTerm: "",
                    showFinalScreen: false,
                    selectedFinalCode: null,
                    currentWarningMessage: null,
                    showError: false,
                    pendingInstructions: [], // NEW: Clear instructions
                    currentInstructionIndex: 0,
                  };
                  reduxDispatch(updateLocalState(newState));
                  reduxDispatch(setRawSearchQuery(""));
                  reduxDispatch(setSearchByCodeId(null));
                  reduxDispatch(handleResetSelectedDetails());
                  reduxDispatch(resetInstructions());
                  reduxDispatch(setInjuryCodeFlowTracker([]));
                  setShowCodeSearch(false);
                  sessionStorage.setItem("newcycle", "false");
                }}
              >
                Cancel
              </Button>
            </div>
          </>
        )}
      </Container>
    );
  }
);

export default CodeSearchUSA;
